import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import SideNavbar from "../../../components/SideNavbar";
import { useAppSelector } from "../../../redux/store";
import { db, auth, rtdb, functions } from "../../../firebaseConfig";
import { collection, collectionGroup, query, where, getDocs, doc, getDoc, serverTimestamp, Timestamp, addDoc } from "firebase/firestore";
import { ref, onValue, off, get, push, set } from "firebase/database";
import { httpsCallable } from 'firebase/functions';

import WhatsappBusinessCloudTemplates from "../../../components/integrations/WhatsappBusinessCloudTemplates";

// functions
import { whatsappTemplateMessage } from "../Admin/Function/WhatsappBusinessCloudClient";

// Simple Toast Component
interface ToastProps {
    message: string;
    type: 'success' | 'error' | 'info';
    onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({ message, type, onClose }) => {
    useEffect(() => {
        const timer = setTimeout(() => {
            onClose();
        }, 3000);
        return () => clearTimeout(timer);
    }, [onClose]);

    const getToastColor = () => {
        switch (type) {
            case 'success':
                return 'bg-green-500';
            case 'error':
                return 'bg-red-500';
            case 'info':
                return 'bg-blue-500';
            default:
                return 'bg-gray-500';
        }
    };

    return (
        <div className={`fixed bottom-4 right-4 ${getToastColor()} text-white px-4 py-3 rounded-md shadow-lg z-50 flex items-center`}>
            <div className="mr-3">{message}</div>
            <button onClick={onClose} className="text-white hover:text-gray-200">
                ×
            </button>
        </div>
    );
};

// Define the phone number document type based on the example provided
interface PhoneNumber {
    assignedUsers: string[];
    companyId: string;
    displayPhoneNumber: string;
    numberId: string;
    qualityRating: string;
    status: string;
    updatedAt: any; // Firestore timestamp
    verifiedName: string;
}

// Define message types
interface WhatsAppMessage {
    id: string;
    from: string;
    to?: string; // Add 'to' field to fix error
    type: 'text' | 'image' | 'video' | 'document' | 'audio' | 'location' | 'template';
    timestamp: string;

    // For text messages
    text?: {
        body: string;
    };

    // For image messages
    image?: {
        id: string;
        mime_type: string;
        sha256: string;
        caption?: string;
        url?: string;
    };

    // For video messages
    video?: {
        id: string;
        mime_type: string;
        sha256: string;
        caption?: string;
        url?: string;
    };

    // For document messages
    document?: {
        id: string;
        mime_type: string;
        sha256: string;
        filename: string;
        url?: string;
        caption?: string; // Add caption field
    };

    // For location messages
    location?: {
        latitude: number;
        longitude: number;
        name?: string;
        address?: string;
    };

    // For audio messages
    audio?: {
        id: string;
        mime_type: string;
        sha256: string;
        url?: string;
    };

    // For template messages
    header?: string;
    body?: string;
    footer?: string;

    // Used for messages sent from the client
    message?: string;
    direction?: 'incoming' | 'outgoing';
    status?: 'sending' | 'sent' | 'delivered' | 'read' | 'failed';
    mediaUrl?: string;
    contactName?: string;
}

interface Contact {
    phoneNumber: string;
    contactName: string;
    lastMessage?: WhatsAppMessage;
    lastIncomingMessage?: WhatsAppMessage; // Added to track the last incoming message for 24h window check
    isOutsideServiceWindow: boolean; // Flag to indicate if the contact is outside the 24-hour service window
    unreadCount?: number;
    phoneNumberId: string; // Added to track which phone number this contact belongs to
    displayPhoneNumber: string; // Added to show user-friendly phone number
    allMessages?: Record<string, any>; // Store all messages data for this contact
}

// Define WhatsApp access token interface
interface WhatsAppAccessToken {
    accountAccessToken: string;
    accountExpiration: any; // Firestore timestamp
    accountId: string;
    accountName: string;
    companyId: string;
    updatedAt: any; // Firestore timestamp
}

// Template types based on WhatsApp Business API
type TemplateCategory = 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
type TemplateStatus = 'PENDING' | 'APPROVED' | 'REJECTED';
type TemplateType = 'TEXT' | 'MEDIA' | 'INTERACTIVE';
type ComponentType = 'HEADER' | 'BODY' | 'FOOTER' | 'BUTTONS';

// Template component interface
interface TemplateComponent {
    type: ComponentType;
    text?: string;
    format?: string;
    example?: {
        header_text?: string[];
        body_text?: string[][];
        header_handle?: string[];
    };
    uploadedFile?: File;
    uploadedFileUrl?: string;
    isUploading?: boolean;
    uploadError?: string;
    uploadedHandle?: string;
}

// Template interface
interface WhatsappTemplate {
    id?: string;
    name: string;
    category: TemplateCategory;
    language: string;
    status?: TemplateStatus;
    components: TemplateComponent[];
    parameter_format?: string;
    createdAt?: any;
    updatedAt?: any;
}

const WhatsappBusinessCloudClient: React.FC = () => {

    const navigate = useNavigate();

    const isCollapsed = useAppSelector((state) => state.sidebar.isCollapsed);
    const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([]);
    const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' | 'info' } | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    // WhatsApp Web Interface States
    // Removed selectedPhoneNumber as we'll now show all phone numbers
    const [contacts, setContacts] = useState<Contact[]>([]);
    const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
    const [messages, setMessages] = useState<WhatsAppMessage[]>([]);
    const [newMessage, setNewMessage] = useState<string>("");
    const [loadingMessages, setLoadingMessages] = useState<boolean>(false);
    const [companyId, setCompanyId] = useState<string>("");
    const [sendingMessage, setSendingMessage] = useState<boolean>(false);
    const [sendMessageError, setSendMessageError] = useState<string>("");
    const [accessToken, setAccessToken] = useState<string | null>(null);
    const [whatsappAccessToken, setWhatsappAccessToken] = useState<WhatsAppAccessToken | null>(null);
    const [expired, setExpired] = useState<boolean>(false);

    // Media message states
    const [showMediaOptions, setShowMediaOptions] = useState<boolean>(false);
    const [selectedMediaType, setSelectedMediaType] = useState<'image' | 'video' | 'document' | null>(null);
    const [uploadingMedia, setUploadingMedia] = useState<boolean>(false);
    const [mediaCaption, setMediaCaption] = useState<string>("");
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploadedMediaId, setUploadedMediaId] = useState<string | null>(null);
    const [mediaPreviewUrl, setMediaPreviewUrl] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // We'll still need to track the selected phone number for sending messages
    const [selectedPhoneNumberInfo, setSelectedPhoneNumberInfo] = useState<PhoneNumber | null>(null);

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const chatContainerRef = useRef<HTMLDivElement>(null);

    // Initialize app when component mounts
    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                // Get the current logged-in user
                const currentUser = auth.currentUser;
                if (!currentUser) {
                    setError("No user logged in");
                    setLoading(false);
                    return;
                }

                // First, get the user's company ID from their user document
                const userDocRef = doc(db, "users", currentUser.uid);
                const userDocSnap = await getDoc(userDocRef);

                if (!userDocSnap.exists()) {
                    setError("User document not found");
                    setLoading(false);
                    return;
                }

                const userData = userDocSnap.data();
                const userCompanyId = userData.companyId;

                if (!userCompanyId) {
                    setError("User is not associated with a company");
                    setLoading(false);
                    return;
                }

                // Store the company ID in the component's state
                setCompanyId(userCompanyId);

                // Query the phoneNumbers collection group for documents with the matching companyId
                // AND where the current user is in the assignedUsers array
                const phoneNumbersQuery = query(
                    collectionGroup(db, "phoneNumbers"),
                    where("companyId", "==", userCompanyId),
                    where("assignedUsers", "array-contains", currentUser.uid)
                );

                const querySnapshot = await getDocs(phoneNumbersQuery);

                const phonesData: PhoneNumber[] = [];
                querySnapshot.forEach((doc) => {
                    phonesData.push(doc.data() as PhoneNumber);
                });

                setPhoneNumbers(phonesData);

                if (phonesData.length > 0) {
                    // Fetch contacts for ALL phone numbers
                    fetchAllContacts(userCompanyId, phonesData);
                }

                setLoading(false);
            } catch (err) {
                console.error("Error fetching initial data:", err);
                setError("Error fetching data");
                setLoading(false);
            }
        };

        fetchInitialData();
    }, []);

    // Fetch WhatsApp access token whenever companyId changes
    useEffect(() => {
        if (!companyId) return;

        const getAccessToken = async () => {
            try {
                await fetchAccessToken();
            } catch (err) {
                console.error("Error fetching access token:", err);
            }
        };

        getAccessToken();
    }, [companyId]);

    // Set up real-time listeners for contacts from all phone numbers
    const setupContactListeners = (companyId: string, phoneNumbers: PhoneNumber[]) => {
        setLoading(true);

        // Initial contacts array to populate
        let initialContactsFetched = false;
        const allContacts: Contact[] = [];

        // Reference to store each contact listener so we can remove them later
        const contactListenerRefs: Array<{ path: string, phoneNumber: PhoneNumber }> = [];

        // Set up a listener for each phone number
        phoneNumbers.forEach(phoneNumber => {
            const contactsRef = ref(rtdb, `whatsapp-messages/${companyId}/${phoneNumber.numberId}/contacts`);
            contactListenerRefs.push({ path: `whatsapp-messages/${companyId}/${phoneNumber.numberId}/contacts`, phoneNumber });

            // Add listener for contacts under this phone number
            onValue(contactsRef, (snapshot) => {
                if (snapshot.exists()) {
                    const contactsData = snapshot.val();
                    const updatedContacts: Contact[] = [];

                    // Process contacts data
                    Object.keys(contactsData).forEach((contactPhone) => {
                        const contactInfo = contactsData[contactPhone];
                        const messagesObj = contactInfo.messages || {};
                        const messageIds = Object.keys(messagesObj);

                        // Find the last message (based on timestamp)
                        let lastMessage: WhatsAppMessage | undefined;
                        let lastIncomingMessage: WhatsAppMessage | undefined;
                        let isOutsideServiceWindow = true; // Default to true - assume outside window unless proven otherwise
                        if (messageIds.length > 0) {
                            // Get messages and filter out any that might not have a timestamp
                            const messagesArray = Object.values(messagesObj) as Record<string, any>[];

                            // Handle case where the messages might be wrapped in a 'message' property
                            const messages = messagesArray
                                .filter(msg => {
                                    return msg && (
                                        (typeof msg.timestamp === 'string') ||
                                        (msg.message && typeof msg.message.timestamp === 'string')
                                    );
                                })
                                .map(item => {
                                    // If there's a nested message object that contains the actual data
                                    if (item.message && typeof item.message === 'object') {
                                        return {
                                            ...item.message,
                                            contactName: item.contactName as string
                                        };
                                    }
                                    return item;
                                }) as WhatsAppMessage[];

                            // Sort by timestamp
                            if (messages.length > 0) {
                                lastMessage = messages.sort((a, b) =>
                                    parseInt(b.timestamp) - parseInt(a.timestamp)
                                )[0];

                                // Find the last incoming message and store it in the variable declared above
                                lastIncomingMessage = messages.filter(msg =>
                                    msg.direction === 'incoming' || (!msg.direction && msg.from !== phoneNumber.numberId)
                                ).sort((a, b) =>
                                    parseInt(b.timestamp) - parseInt(a.timestamp)
                                )[0];

                                // Check if the contact is outside the 24-hour service window
                                if (lastIncomingMessage) {
                                    // Get the current server timestamp
                                    const now = Timestamp.now();
                                    // Get the timestamp of the last incoming message in seconds
                                    const lastIncomingTimestamp = parseInt(lastIncomingMessage.timestamp);
                                    // Convert the timestamp to a Firestore Timestamp
                                    const lastMessageTime = Timestamp.fromMillis(lastIncomingTimestamp * 1000);
                                    // Calculate the difference in seconds
                                    const diffInSeconds = now.seconds - lastMessageTime.seconds;
                                    // Convert to hours
                                    const hoursDifference = diffInSeconds / 3600;
                                    // If the difference is more than 24 hours, the contact is outside the service window
                                    isOutsideServiceWindow = hoursDifference > 24;
                                } // else isOutsideServiceWindow remains true (default)

                                // Use contactName from the last message if available, then try last incoming message,
                                // then fallback to the contact info name or phone number
                                const contactName =
                                    (lastMessage && lastMessage.contactName) ||
                                    (lastIncomingMessage && lastIncomingMessage.contactName) ||
                                    contactInfo.contactName ||
                                    contactPhone;

                                // Update contact info with the potentially new contact name
                                contactInfo.contactName = contactName;

                                // Check if the contact is outside the 24-hour service window
                                // Variable already declared at outer scope, just assign new value if needed
                                if (lastIncomingMessage) {
                                    // Get the current timestamp in seconds
                                    const nowInSeconds = Math.floor(Date.now() / 1000);
                                    // Get the timestamp of the last incoming message in seconds
                                    const lastIncomingTimestamp = parseInt(lastIncomingMessage.timestamp);
                                    // Calculate the difference in hours
                                    const hoursDifference = (nowInSeconds - lastIncomingTimestamp) / 3600;
                                    // If the difference is more than 24 hours, the contact is outside the service window
                                    isOutsideServiceWindow = hoursDifference > 24;
                                } else {
                                    // If there's no incoming message at all, consider it outside the service window
                                    isOutsideServiceWindow = true;
                                }
                            }
                        }

                        // Use the updated contactInfo
                        const contactName = contactInfo.contactName || contactPhone;

                        // Create contact object with all message data
                        const contactObject = {
                            phoneNumber: contactPhone,
                            contactName: contactName,
                            lastMessage: lastMessage,
                            lastIncomingMessage: lastIncomingMessage, // Store the last incoming message
                            isOutsideServiceWindow: isOutsideServiceWindow, // Add the service window flag
                            unreadCount: 0, // You could calculate this if needed
                            phoneNumberId: phoneNumber.numberId,
                            displayPhoneNumber: phoneNumber.displayPhoneNumber,
                            allMessages: messagesObj // Store all messages data for this contact
                        };

                        updatedContacts.push(contactObject);

                        // Check if this is the currently selected contact
                        const isCurrentlySelected = selectedContact &&
                            selectedContact.phoneNumber === contactPhone &&
                            selectedContact.phoneNumberId === phoneNumber.numberId;

                        // If this is the currently selected contact, update its messages in real-time
                        if (isCurrentlySelected) {
                            // Update the messages display with new data
                            processAndSetMessages(messagesObj, contactObject);
                        } else if (messagesObj) {
                            // This contact has new messages but is not currently selected
                            // Could add unread message count or notification here
                            // contactObject.unreadCount = ...
                        }
                    });

                    // For the initial load, collect contacts from all phone numbers
                    if (!initialContactsFetched) {
                        allContacts.push(...updatedContacts);
                    } else {
                        // After initial load, update the contacts list with real-time changes
                        // Get current contacts
                        const currentContacts = [...contacts];

                        // Remove contacts belonging to this phone number
                        const otherPhoneContacts = currentContacts.filter(c => c.phoneNumberId !== phoneNumber.numberId);

                        // Add updated contacts
                        const mergedContacts = [...otherPhoneContacts, ...updatedContacts];

                        // Sort and update state
                        mergedContacts.sort((a, b) => {
                            const aTime = a.lastMessage ? parseInt(a.lastMessage.timestamp) : 0;
                            const bTime = b.lastMessage ? parseInt(b.lastMessage.timestamp) : 0;
                            return bTime - aTime;
                        });

                        // Update contacts without changing the selected contact
                        setContacts(mergedContacts);

                        // For contacts that received a new message but aren't selected, we can 
                        // highlight them or indicate they have unread messages here if needed
                    }
                }

                // After all phone numbers are processed for the first time
                if (!initialContactsFetched && phoneNumber.numberId === phoneNumbers[phoneNumbers.length - 1].numberId) {
                    initialContactsFetched = true;

                    // Sort all contacts by last message timestamp (newest first)
                    allContacts.sort((a, b) => {
                        const aTime = a.lastMessage ? parseInt(a.lastMessage.timestamp) : 0;
                        const bTime = b.lastMessage ? parseInt(b.lastMessage.timestamp) : 0;
                        return bTime - aTime;
                    });

                    setContacts(allContacts);

                    setLoading(false);
                }
            }, (error) => {
                console.error(`Error setting up contact listener for ${phoneNumber.displayPhoneNumber}:`, error);
                if (!initialContactsFetched) {
                    initialContactsFetched = true;
                    setLoading(false);
                }
            });
        });

        // Return a cleanup function to remove all contact listeners
        return () => {
            contactListenerRefs.forEach(refInfo => {
                // Create a proper reference object from the path
                const contactsRefObj = ref(rtdb, refInfo.path);

                // Remove the listener
                off(contactsRefObj);
            });
        };
    };

    // Initial fetch of contacts and setup of listeners
    const fetchAllContacts = async (companyId: string, phoneNumbers: PhoneNumber[]) => {
        try {
            // Set up listeners for all contacts
            setupContactListeners(companyId, phoneNumbers);
        } catch (err) {
            console.error("Error setting up contact listeners:", err);
            setLoading(false);
        }
    };

    // No longer needed - using real-time listeners instead

    // Process messages and set them in state
    const processAndSetMessages = (messagesData: any, contact: Contact) => {
        // Define a type for the raw message data from Firebase
        interface RawMessageData {
            message?: {
                from?: string;
                id?: string;
                type?: 'text' | 'image' | 'video' | 'document' | 'audio' | 'location' | 'template';
                timestamp?: string;
                text?: { body: string };
                image?: {
                    id: string;
                    mime_type: string;
                    sha256: string;
                    caption?: string;
                    url?: string;
                };
                video?: {
                    id: string;
                    mime_type: string;
                    sha256: string;
                    caption?: string;
                    url?: string;
                };
                document?: {
                    id: string;
                    mime_type: string;
                    sha256: string;
                    filename: string;
                    url?: string;
                    caption?: string; // Add caption field
                };
                location?: {
                    latitude: number;
                    longitude: number;
                    name?: string;
                    address?: string;
                };
                audio?: {
                    id: string;
                    mime_type: string;
                    sha256: string;
                    url?: string;
                };
            };
            contactName?: string;
            from?: string;
            id?: string;
            timestamp?: string;
            type?: 'text' | 'image' | 'video' | 'document' | 'audio' | 'location' | 'template';
            text?: { body: string };
            image?: {
                id: string;
                mime_type: string;
                sha256: string;
                caption?: string;
                url?: string;
            };
            video?: {
                id: string;
                mime_type: string;
                sha256: string;
                caption?: string;
                url?: string;
            };
            document?: {
                id: string;
                mime_type: string;
                sha256: string;
                filename: string;
                url?: string;
                caption?: string; // Add caption field
            };
            location?: {
                latitude: number;
                longitude: number;
                name?: string;
                address?: string;
            };
            audio?: {
                id: string;
                mime_type: string;
                sha256: string;
                url?: string;
            };
            headerText?: string;
            body?: string;
            footer?: string;
            direction?: 'incoming' | 'outgoing';
            status?: 'sending' | 'sent' | 'delivered' | 'read' | 'failed';
        }

        // Extract and normalize message objects
        const messagesArray = Object.values(messagesData) as RawMessageData[];
        const messagesList: WhatsAppMessage[] = messagesArray
            .filter((msg): msg is RawMessageData => {
                if (!msg) return false;

                // Check if msg has a valid timestamp property
                const hasValidTimestamp = msg.timestamp !== undefined &&
                    typeof msg.timestamp === 'string';

                // Check if msg has a nested message with a valid timestamp
                const hasValidNestedTimestamp = msg.message !== undefined &&
                    msg.message.timestamp !== undefined &&
                    typeof msg.message.timestamp === 'string';

                return hasValidTimestamp || hasValidNestedTimestamp;
            })
            .map(item => {
                // If there's a nested message property that contains the actual data
                if (item.message && typeof item.message === 'object') {
                    // Copy basic properties
                    const messageObj: WhatsAppMessage = {
                        id: item.message.id || '',
                        from: item.message.from || '',
                        type: item.message.type || 'text',
                        timestamp: item.message.timestamp || '',
                        direction: 'incoming' // Messages from WhatsApp API are incoming by default
                    };

                    // Copy specific message type data
                    if (messageObj.type === 'text' && item.message.text) {
                        messageObj.text = item.message.text;
                    } else if (messageObj.type === 'image' && item.message.image) {
                        messageObj.image = item.message.image;
                    } else if (messageObj.type === 'video' && item.message.video) {
                        messageObj.video = item.message.video;
                    } else if (messageObj.type === 'document' && item.message.document) {
                        messageObj.document = item.message.document;
                    } else if (messageObj.type === 'location' && item.message.location) {
                        messageObj.location = item.message.location;
                    } else if (messageObj.type === 'audio' && item.message.audio) {
                        messageObj.audio = item.message.audio;
                    } else if (messageObj.type === 'template' && item.headerText) {
                        messageObj.header = item.headerText;
                        messageObj.body = item.body;
                        messageObj.footer = item.footer;
                    } else if (messageObj.type === 'template' && item.document) {
                        messageObj.document = item.document;
                        messageObj.body = item.body;
                        messageObj.footer = item.footer;
                    } else if (messageObj.type === 'template' && item.image) {
                        messageObj.image = item.image;
                        messageObj.body = item.body;
                        messageObj.footer = item.footer;
                    } else if (messageObj.type === 'template' && item.video) {
                        messageObj.video = item.video;
                        messageObj.body = item.body;
                        messageObj.footer = item.footer;
                    }

                    // Add contact name if available
                    if (item.contactName) messageObj.contactName = item.contactName;

                    return messageObj;
                }

                // Create a properly typed message object for direct message structure
                const messageObj: WhatsAppMessage = {
                    id: item.id || '',
                    from: item.from || '',
                    type: item.type || 'text',
                    timestamp: item.timestamp || '',
                    direction: item.direction,
                    status: item.status // Copy status for delivery receipts
                };

                // Copy specific message type data
                if (messageObj.type === 'text' && item.text) {
                    messageObj.text = item.text;
                } else if (messageObj.type === 'image' && item.image) {
                    messageObj.image = item.image;
                } else if (messageObj.type === 'video' && item.video) {
                    messageObj.video = item.video;
                } else if (messageObj.type === 'document' && item.document) {
                    messageObj.document = item.document;
                } else if (messageObj.type === 'location' && item.location) {
                    messageObj.location = item.location;
                } else if (messageObj.type === 'audio' && item.audio) {
                    messageObj.audio = item.audio;
                } else if (messageObj.type === 'template' && item.headerText) {
                    messageObj.header = item.headerText;
                    messageObj.body = item.body;
                    messageObj.footer = item.footer;
                } else if (messageObj.type === 'template' && item.document) {
                    messageObj.document = item.document;
                    messageObj.body = item.body;
                    messageObj.footer = item.footer;
                } else if (messageObj.type === 'template' && item.image) {
                    messageObj.image = item.image;
                    messageObj.body = item.body;
                    messageObj.footer = item.footer;
                } else if (messageObj.type === 'template' && item.video) {
                    messageObj.video = item.video;
                    messageObj.body = item.body;
                    messageObj.footer = item.footer;
                }

                // Add direction for proper display
                if (!messageObj.direction) {
                    // If 'from' matches the contact's number, it's incoming
                    messageObj.direction = messageObj.from === contact.phoneNumber ? 'incoming' : 'outgoing';
                }

                return messageObj;
            });

        // Sort messages by timestamp (oldest first for chat display)
        messagesList.sort((a, b) => parseInt(a.timestamp) - parseInt(b.timestamp));

        console.log("Processed messages:", messagesList);

        setMessages(messagesList);

        // Scroll to bottom of messages
        setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
    };

    // Setup real-time listener for the currently selected conversation's messages
    const setupCurrentConversationListener = (contact: Contact) => {
        if (!contact || !companyId) return;

        setLoadingMessages(true);

        // First, show existing messages immediately (if we have them)
        if (contact.allMessages && Object.keys(contact.allMessages).length > 0) {
            // Use the messages data that came with the contact
            processAndSetMessages(contact.allMessages, contact);
            setLoadingMessages(false);
        } else {
            // If no messages, show empty array
            setMessages([]);
            setLoadingMessages(false);
        }

        // Create a reference specifically for this conversation's messages
        const currentConversationRef = ref(
            rtdb,
            `whatsapp-messages/${companyId}/${contact.phoneNumberId}/contacts/${contact.phoneNumber}/messages`
        );

        // Remove any existing listener to avoid duplicates
        off(currentConversationRef);

        // Set up dedicated listener for this conversation
        onValue(currentConversationRef, (snapshot) => {
            if (snapshot.exists()) {
                processAndSetMessages(snapshot.val(), contact);
            } else {
                setMessages([]);
            }
        }, (error) => {
            console.error("Error in conversation listener:", error);
        });
    };

    // Handle contact selection
    const handleContactSelect = (contact: Contact) => {
        setSelectedContact(contact);

        // Set the selected phone number info for the current contact
        // This is needed for sending messages
        const phoneNumber = phoneNumbers.find(phone => phone.numberId === contact.phoneNumberId);
        if (phoneNumber) {
            setSelectedPhoneNumberInfo(phoneNumber);
        }

        // Set up real-time listener for this specific conversation
        setupCurrentConversationListener(contact);
    };

    // Fetch WhatsApp access token
    const fetchAccessToken = async () => {
        if (!companyId) return null;

        try {
            // Query the whatsappBusinessCloudAccounts collection for this company
            const accessTokenQuery = query(
                collection(db, "whatsappBusinessCloudAccounts"),
                where("companyId", "==", companyId)
            );

            const querySnapshot = await getDocs(accessTokenQuery);

            if (querySnapshot.empty) {
                console.error("No WhatsApp access token found for this company");
                return null;
            }

            // Get the first matching document
            const tokenData = querySnapshot.docs[0].data() as WhatsAppAccessToken;

            setWhatsappAccessToken(tokenData);
            console.log("WhatsApp access token set:", tokenData);

            // Check if token is expired
            const expiration = tokenData.accountExpiration?.toDate ?
                tokenData.accountExpiration.toDate() :
                new Date(tokenData.accountExpiration);

            if (expiration < new Date()) {
                console.error("WhatsApp access token has expired");
                setExpired(true);
                return null;
            }

            // Set the token in state
            setAccessToken(tokenData.accountAccessToken);
            return tokenData.accountAccessToken;

        } catch (err) {
            console.error("Error fetching WhatsApp access token:", err);
            return null;
        }
    };

    // Cloud functions for WhatsApp operations
    const sendWhatsAppMessageFunction = httpsCallable(functions, 'sendWhatsAppMessage');
    const uploadWhatsAppMediaFunction = httpsCallable(functions, 'uploadWhatsAppMedia');
    const sendWhatsAppTemplateMessage = httpsCallable(functions, 'sendWhatsAppTemplateMessage');

    // Handle media option selection
    const handleMediaOptionClick = (type: 'image' | 'video' | 'document') => {
        setSelectedMediaType(type);
        setShowMediaOptions(false);

        // Clear any previous selection
        setSelectedFile(null);
        setUploadedMediaId(null);
        setMediaPreviewUrl(null);

        // Trigger file input click
        setTimeout(() => {
            if (fileInputRef.current) {
                fileInputRef.current.accept = type === 'image' ? 'image/*' :
                    type === 'video' ? 'video/*' :
                        '.pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx';
                fileInputRef.current.click();
            }
        }, 100);
    };

    // File to base64 conversion utility
    const fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = error => reject(error);
        });
    };

    // File selection and media message handling already uses the state declared above

    // Handle file selection for media messages
    const handleFileSelection = async (e: React.ChangeEvent<HTMLInputElement>) => {
        // Check if a file was selected
        const file = e.target.files?.[0];
        if (!file || !selectedMediaType || !selectedContact || !selectedPhoneNumberInfo || !companyId) {
            // No file selected or missing required data
            console.log("File selection canceled or missing data");
            setSelectedMediaType(null);
            return;
        }

        // Store the selected file for later sending
        setSelectedFile(file);

        // Generate preview URL for the file
        if (selectedMediaType === 'image') {
            const previewUrl = URL.createObjectURL(file);
            setMediaPreviewUrl(previewUrl);
        } else if (selectedMediaType === 'video') {
            const previewUrl = URL.createObjectURL(file);
            setMediaPreviewUrl(previewUrl);
        } else {
            // For documents, we'll just show an icon in the UI
            setMediaPreviewUrl(null);
        }

        try {

            setUploadingMedia(true);

            // Get access token if not already available
            const token = accessToken || await fetchAccessToken();

            if (!token) {
                throw new Error("Could not get access token for WhatsApp API");
            }

            // Convert file to base64
            const base64Data = await fileToBase64(file);
            // Remove the data URL prefix (e.g., 'data:image/jpeg;base64,')
            const base64Content = base64Data.split(',')[1];

            // Upload media to WhatsApp
            const uploadResult = await uploadWhatsAppMediaFunction({
                accessToken: token,
                accountPhoneNumberId: selectedPhoneNumberInfo.numberId,
                mediaType: selectedMediaType,
                mediaData: base64Content,
                mimeType: file.type,
                filename: file.name
            });

            const { success, mediaId } = uploadResult.data as {
                success: boolean;
                mediaId: string;
            };

            if (!success || !mediaId) {
                throw new Error("Failed to upload media to WhatsApp");
            }

            // Store the media ID for later sending
            setUploadedMediaId(mediaId);
            setUploadingMedia(false);

            // Show a toast indicating media is ready to send
            setToast({
                show: true,
                message: `${selectedMediaType} uploaded successfully. Add a caption if you wish, then click send.`,
                type: "success"
            });

        } catch (err) {
            console.error("Error uploading media:", err);
            setUploadingMedia(false);
            setSelectedMediaType(null);
            setSelectedFile(null);
            setUploadedMediaId(null);

            // Show error toast
            setToast({
                show: true,
                message: "Failed to upload media. Please try again.",
                type: "error"
            });
        }
    };

    // Separate function to send the media message after upload
    const sendMediaMessage = async () => {
        if (!selectedContact || !selectedPhoneNumberInfo || !selectedMediaType || !uploadedMediaId) return;

        try {
            setSendingMessage(true);

            // Use the main input text as the caption
            const caption = newMessage.trim();

            // Get access token if not already available
            const token = accessToken || await fetchAccessToken();

            if (!token) {
                throw new Error("Could not get access token for WhatsApp API");
            }

            console.log("Sending media message with caption:", caption);

            // Create temporary message for UI
            const tempMessageId = `temp-${Date.now()}`;
            const tempMessage: WhatsAppMessage = {
                id: tempMessageId,
                from: selectedPhoneNumberInfo.numberId,
                to: selectedContact.phoneNumber,
                type: selectedMediaType,
                timestamp: Math.floor(Date.now() / 1000).toString(),
                [selectedMediaType]: {
                    id: uploadedMediaId,
                    caption: caption || undefined
                },
                direction: 'outgoing',
                status: 'sending'
            };

            // Update UI to show sending message
            setMessages(prevMessages => [...prevMessages, tempMessage]);

            // Send the media message
            const result = await sendWhatsAppMessageFunction({
                to: selectedContact.phoneNumber,
                message: '', // Not used for media messages
                type: selectedMediaType,
                mediaId: uploadedMediaId,
                caption: caption || undefined,
                accessToken: token,
                accountPhoneNumberId: selectedPhoneNumberInfo.numberId,
                companyId,
                // Pass filename for document messages
                filename: selectedFile?.name
            });

            // Reset media states
            setSelectedMediaType(null);
            setSelectedFile(null);
            setUploadedMediaId(null);
            setMediaPreviewUrl(null);
            setNewMessage(""); // Clear the caption/message input

            showToast("Media sent successfully!", "success");

            // Scroll to bottom of messages
            setTimeout(() => {
                messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            }, 100);

        } catch (err) {
            console.error("Error sending media message:", err);
            setSendMessageError("Failed to send media message");
            // Show error toast
            setToast({
                show: true,
                message: "Failed to send media. Please try again.",
                type: "error"
            });
        } finally {
            setSendingMessage(false);
        }
    };

    // Send a new message
    const sendMessage = async () => {
        // If there's media attached, use the media message function instead
        if (selectedMediaType && uploadedMediaId) {
            return sendMediaMessage();
        }

        if (!selectedContact || !selectedPhoneNumberInfo || !companyId || !newMessage.trim()) {
            return;
        }

        try {
            // Clear previous errors
            setSendMessageError("");
            setSendingMessage(true);

            // Get access token if not already available
            const token = accessToken || await fetchAccessToken();

            if (!token) {
                throw new Error("Could not get access token for WhatsApp API");
            }

            // Show sending indicator in UI only
            const tempMessageId = `temp-${Date.now()}`;
            const tempMessage: WhatsAppMessage = {
                id: tempMessageId,
                from: selectedPhoneNumberInfo.numberId,
                to: selectedContact.phoneNumber,
                type: 'text',
                timestamp: Math.floor(Date.now() / 1000).toString(),
                text: {
                    body: newMessage.trim()
                },
                direction: 'outgoing',
                status: 'sending'
            };

            // Update UI to show sending message
            setMessages([...messages, tempMessage]);

            console.log("Sending message with:", {
                to: selectedContact.phoneNumber,
                message: newMessage.trim(),
                accessToken: token,
                accountPhoneNumberId: selectedPhoneNumberInfo.numberId,
                companyId
            });

            // Send the message via the Cloud Function
            // The Cloud Function will handle storing the message in the database
            const result = await sendWhatsAppMessageFunction({
                to: selectedContact.phoneNumber,
                message: newMessage.trim(),
                type: 'text',
                accessToken: token,
                accountPhoneNumberId: selectedPhoneNumberInfo.numberId,
                companyId
            });

            const { success } = result.data as {
                success: boolean;
                messageId: string;
                cleanId: string;
            };

            if (!success) {
                throw new Error("Failed to send message through WhatsApp API");
            }

            // No need to update the database here as the Cloud Function handles it
            // Just clear the message input and show success in the UI
            setNewMessage("");

            // The UI will be updated automatically via the Realtime Database listeners
            // when the new message is stored by the Cloud Function

            // Scroll to the bottom of the chat
            setTimeout(() => {
                messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            }, 100);

            // Show success message
            setToast({
                show: true,
                message: "Message sent successfully",
                type: "success"
            });

        } catch (err: any) {
            console.error("Error sending message:", err);
            setToast({
                show: true,
                message: `Failed to send message: ${err.message}`,
                type: "error"
            });
            setSendMessageError(err instanceof Error ? err.message : "An unknown error occurred");
        } finally {
            setSendingMessage(false);
        }
    };

    // Format timestamp to readable date
    const formatDate = (timestamp: any) => {
        if (!timestamp) return "N/A";
        try {
            // Convert Firestore timestamp to JavaScript Date
            const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
            return date.toLocaleString();
        } catch (error) {
            return "Invalid date";
        }
    };

    // Format a timestamp for display in a user-friendly way
    const formatMessageTime = (timestamp: any): string => {
        if (!timestamp) return '';

        try {
            // Handle different timestamp formats
            let date: Date;

            if (typeof timestamp === 'object') {
                if (timestamp.seconds) {
                    // Firebase Timestamp
                    date = new Date(timestamp.seconds * 1000);
                } else if (timestamp.toDate) {
                    // Firestore Timestamp with toDate method
                    return timestamp.toDate().toLocaleString();
                } else {
                    // Regular Date object
                    date = new Date(timestamp);
                }
            } else if (typeof timestamp === 'number') {
                // Unix timestamp in seconds or milliseconds
                // Check if it's seconds (typically 10 digits) or milliseconds (typically 13 digits)
                date = timestamp > 9999999999 ? new Date(timestamp) : new Date(timestamp * 1000);
            } else if (typeof timestamp === 'string') {
                // ISO string or other string format
                if (!isNaN(Number(timestamp))) {
                    // If it's a numeric string, treat as milliseconds
                    const num = Number(timestamp);
                    date = num > 9999999999 ? new Date(num) : new Date(num * 1000);
                } else {
                    // Otherwise parse as ISO string
                    date = new Date(timestamp);
                }
            } else {
                console.log("Unknown timestamp format:", timestamp);
                return '';
            }

            // Check if the date is valid
            if (isNaN(date.getTime())) {
                console.log("Invalid date from timestamp:", timestamp);
                return '';
            }

            // Format the date showing both date and time
            return date.toLocaleString([], {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            console.error("Error formatting date:", error, "Timestamp:", timestamp);
            return '';
        }
    };

    // Handle sending message with Enter key
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();

            // Check if we should send media message or text message
            if (selectedMediaType && uploadedMediaId) {
                sendMediaMessage();
            } else if (newMessage.trim()) {
                sendMessage();
            }
        }
    };

    // Show toast notification
    const showToast = (message: string, type: 'success' | 'error' | 'info') => {
        setToast({ show: true, message, type });
    };

    // Hide toast notification
    const hideToast = () => {
        setToast(null);
    };

    // Set up contact listeners when component mounts, clean up when unmounts
    useEffect(() => {
        let contactListenersCleanup: (() => void) | null = null;

        const setupInitialListeners = async () => {
            if (!companyId || phoneNumbers.length === 0) return;

            // Set up listeners for all contacts
            contactListenersCleanup = setupContactListeners(companyId, phoneNumbers);
        };

        // Set up listeners when we have phone numbers and a company ID
        if (phoneNumbers.length > 0 && companyId) {
            setupInitialListeners();
        }

        return () => {
            // Only clean up contact listeners when component unmounts or
            // when companyId/phoneNumbers change
            if (contactListenersCleanup) {
                contactListenersCleanup();
            }
        };
    }, [companyId, phoneNumbers]);

    // Separate useEffect for conversation listener cleanup
    useEffect(() => {
        return () => {
            // Clean up conversation listener when selected contact changes
            if (selectedContact && companyId) {
                const conversationRef = ref(
                    rtdb,
                    `whatsapp-messages/${companyId}/${selectedContact.phoneNumberId}/contacts/${selectedContact.phoneNumber}/messages`
                );
                off(conversationRef);
            }
        };
    }, [selectedContact, companyId]);

    const [searchTerm, setSearchTerm] = useState('');

    const getLastMessagePreview = (message: WhatsAppMessage | undefined) => {
        if (!message) return '';
        switch (message.type) {
            case 'text':
                return message.text?.body || '';
            case 'image':
                return message.image?.caption || '🖼️ Image';
            case 'video':
                return message.video?.caption || '🎥 Video';
            case 'document':
                return message.document?.filename || '📄 Document';
            case 'location':
                return '📍 Location';
            case 'audio':
                return '🔊 Audio';
            default:
                return '';
        }
    };

    // Cloud function for downloading WhatsApp media
    const downloadWhatsAppMediaFunction = httpsCallable(functions, 'downloadWhatsAppMedia');

    // Function to handle media download through Cloud Functions
    // Keep track of audio media URLs for playback
    const [audioMediaUrls, setAudioMediaUrls] = useState<Record<string, string>>({});

    const handleMediaDownload = async (mediaId: string, mediaType: string, filename?: string) => {
        try {
            // Ensure access token is available
            const token = accessToken || await fetchAccessToken();

            if (!token) {
                showToast("Could not get access token for WhatsApp API", "error");
                return;
            }

            // Show a loading indicator
            showToast("Retrieving media... Please wait.", "info");

            // Call the cloud function to download the media
            const result = await downloadWhatsAppMediaFunction({
                mediaId,
                accessToken: token,
                mediaType,
                filename
            });

            // Parse the response data
            const { data } = result.data as {
                data: string;  // base64 data
                mimeType: string;
                filename: string;
            };

            if (!data) {
                showToast("Error: No data received from server", "error");
                return;
            }

            // Create a Blob with the base64 data
            const binaryData = atob(data);
            const bytes = new Uint8Array(binaryData.length);
            for (let i = 0; i < binaryData.length; i++) {
                bytes[i] = binaryData.charCodeAt(i);
            }
            const blob = new Blob([bytes], { type: (result.data as any).mimeType });
            const url = URL.createObjectURL(blob);

            // For audio files, store the URL for playback instead of downloading
            if (mediaType === 'audio') {
                // Store the URL in state so it can be used by the audio player
                setAudioMediaUrls(prev => ({
                    ...prev,
                    [mediaId]: url
                }));

                // Notify user
                showToast("Audio ready to play!", "success");
            } else {
                // For non-audio files, create download link as before
                const a = document.createElement('a');
                a.href = url;
                a.download = (result.data as any).filename || `whatsapp_${mediaType}_${Date.now()}`;
                document.body.appendChild(a);
                a.click();

                // Clean up
                setTimeout(() => {
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                }, 100);

                // Notify user
                showToast("Media downloaded successfully!", "success");
            }

        } catch (error: unknown) {
            console.error("Error downloading media:", error);
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            showToast(`Error downloading media: ${errorMessage}`, "error");
        }
    };

    const isSendingDisabled = () => {
        return (
            sendingMessage ||
            uploadingMedia ||
            selectedContact?.isOutsideServiceWindow ||
            expired ||
            !selectedContact ||
            !selectedPhoneNumberInfo ||
            !companyId
        );
    };

    const handleCancelMedia = () => {
        setSelectedMediaType(null);
        setMediaCaption("");
        setSelectedFile(null);
        setUploadedMediaId(null);
        setMediaPreviewUrl(null);
    };

    const getInputPlaceholder = () => {
        if (selectedContact?.isOutsideServiceWindow) {
            return "Cannot send messages outside 24-hour window";
        }

        if (selectedMediaType) {
            return "Type a caption...";
        }

        return "Type a message";
    };

    const handleSendClick = () => {
        if (selectedMediaType && uploadedMediaId) {
            sendMediaMessage();
        } else {
            sendMessage();
        }
    };

    interface AddLead {
        name: string;
        phoneNumber: string;
        companyId: string;
        assignedTo: string;
        sourceId: string;
        campaignId: string;
        remarks: string;
    }

    const [checkNumberExists, setCheckNumberExists] = useState(true);
    const [checkNumberLoading, setCheckNumberLoading] = useState(false);
    const [showAddLeadModal, setShowAddLeadModal] = useState(false);
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [sources, setSources] = useState<any[]>([]);
    const [campaigns, setCampaigns] = useState<any[]>([]);

    const [addLeadData, setAddLeadData] = useState<AddLead>({
        name: "",
        phoneNumber: "",
        companyId: "",
        assignedTo: "",
        sourceId: "",
        campaignId: "",
        remarks: ""
    });

    const handleCheckNumberExists = async () => {
        if (!selectedContact) return;
        setCheckNumberLoading(true);

        try {
            const user = auth.currentUser;
            if (!user) {
                console.error("User not authenticated");
                return;
            }

            const userRef = doc(db, "users", user.uid);
            const userDoc = await getDoc(userRef);
            if (!userDoc.exists()) {
                console.error("User document not found");
                return;
            }

            // Get the company ID from the user document
            const userData = userDoc.data();
            const companyId = userData.companyId;

            if (!companyId) {
                console.error("Company ID not found in user document");
                return;
            }

            // check if the number exists in leads sub collection in companies collection
            const leadsRef = collection(db, "companies", companyId, "leads");
            const leadsQuery = query(leadsRef, where("number", "==", cleanPhoneNumber(selectedContact.phoneNumber)));
            const leadsSnapshot = await getDocs(leadsQuery);

            if (leadsSnapshot.empty) {
                console.log("Number does not exist in leads collection");
                setCheckNumberExists(false);
                setCheckNumberLoading(false);
                return;
            } else {
                console.log("Number exists in leads collection");
                setCheckNumberExists(true);
                setCheckNumberLoading(false);
            }
        } catch (error) {
            console.error("Error checking number:", error);
            setCheckNumberLoading(false);
        }
    };

    useEffect(() => {
        if (selectedContact) {
            handleCheckNumberExists();
            setShowAddLeadModal(false);
        }
    }, [selectedContact]);

    // Clean up phone number
    const cleanPhoneNumber = (phone: string): string => {
        const cleanNumber = phone?.replace(/\D/g, '');

        if (cleanNumber.length === 10) {
            return `+91${cleanNumber}`;
        } else if (cleanNumber.length > 10) {
            return `+${cleanNumber}`;
        } else {
            return cleanNumber;
        }
    };

    const validatePhoneNumber = (phone: string): boolean => {
        return phone.length >= 10 && phone.startsWith("+");
    }

    const handleAddLeadClick = async () => {
        if (!selectedContact) return;

        const user = auth.currentUser;
        if (!user) {
            console.error("User not authenticated");
            return;
        }

        setAddLeadData({
            name: selectedContact.contactName.toLocaleUpperCase(),
            phoneNumber: cleanPhoneNumber(selectedContact.phoneNumber),
            companyId: companyId || "",
            assignedTo: user?.uid || "",
            sourceId: "",
            campaignId: "",
            remarks: ""
        });

        // fetch sources from the database
        const sourcesSnapshot = await getDocs(collection(db, `companies/${companyId}/leadSources`));
        const sourcesList = sourcesSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
        setSources(sourcesList);


        // fetch campaigns from the database
        const campaignsSnapshot = await getDocs(collection(db, `companies/${companyId}/leadCampaigns`));
        const campaignsList = campaignsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
        setCampaigns(campaignsList);;


        setShowAddLeadModal(true);
    };

    const handleChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target;
        const start = input.selectionStart ?? 0;
        const end = input.selectionEnd ?? 0;
        const upperCased = input.value.toLocaleUpperCase();

        setAddLeadData({
            ...addLeadData,
            name: upperCased
        });

        // Set the cursor position back to where it was
        setTimeout(() => {
            input.setSelectionRange(start, end);
        }, 0);
    }

    const handleChangePhoneNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target;
        const start = input.selectionStart ?? 0;
        const end = input.selectionEnd ?? 0;
        const number = input.value;

        setAddLeadData({
            ...addLeadData,
            phoneNumber: number
        });

        // Set the cursor position back to where it was
        setTimeout(() => {
            input.setSelectionRange(start, end);
        }, 0);
    }

    const handleAddLeadSubmit = async () => {
        if (!addLeadData) return;

        try {
            // Validate user authentication
            const user = auth.currentUser;
            if (!user) {
                setToast({
                    show: true,
                    message: "Authentication error. Please log in again.",
                    type: "error"
                });
                return;
            }

            // Get user data and company ID
            const userRef = doc(db, "users", user.uid);
            const userDoc = await getDoc(userRef);

            if (!userDoc.exists()) {
                setToast({
                    show: true,
                    message: "User profile not found. Please contact support.",
                    type: "error"
                });
                return;
            }

            const userData = userDoc.data();
            const companyId = userData.companyId;

            if (!companyId) {
                setToast({
                    show: true,
                    message: "Company not assigned. Please contact your administrator.",
                    type: "error"
                });
                return;
            }

            // Validate required fields
            const requiredFields = [
                { name: 'name', label: 'Name' },
                { name: 'phoneNumber', label: 'Phone Number' },
                { name: 'assignedTo', label: 'Assigned To' },
                { name: 'sourceId', label: 'Source' },
                { name: 'campaignId', label: 'Campaign' }
            ];

            const missingFields = requiredFields
                .filter(field => !addLeadData[field.name as keyof AddLead])
                .map(field => field.label);

            if (missingFields.length > 0) {
                setToast({
                    show: true,
                    message: `Please fill the following fields: ${missingFields.join(', ')}`,
                    type: "error"
                });
                return;
            }

            // Validate phone number
            const cleanedPhoneNumber = cleanPhoneNumber(addLeadData.phoneNumber);
            if (!validatePhoneNumber(cleanedPhoneNumber)) {
                setToast({
                    show: true,
                    message: "Please enter a valid phone number",
                    type: "error"
                });
                return;
            }

            // Prepare lead data
            const formattedData = {
                name: addLeadData.name.toLocaleUpperCase(),
                number: cleanedPhoneNumber,
                companyId: companyId,
                assignedTo: addLeadData.assignedTo,
                sourceId: addLeadData.sourceId,
                campaignId: addLeadData.campaignId,
                isActive: true,
                createdBy: user.uid,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            };

            console.log("Adding lead data:", formattedData);


            // Save lead data to database
            const leadsCollectionRef = collection(db, `companies/${companyId}/leads`);
            const leadDocRef = await addDoc(leadsCollectionRef, formattedData);

            // Add remarks if provided
            if (addLeadData.remarks?.trim()) {
                const remarksCollectionRef = collection(leadDocRef, "remarks");
                await addDoc(remarksCollectionRef, {
                    remark: addLeadData.remarks.trim(),
                    createdAt: serverTimestamp()
                });
            }

            // Show success message
            setToast({
                show: true,
                message: "Lead added successfully",
                type: "success"
            });

            return leadDocRef.id;
        } catch (error) {
            console.error("Error adding lead:", error);
            setToast({
                show: true,
                message: `Failed to add lead: ${error instanceof Error ? error.message : "Unknown error"}`,
                type: "error"
            });
        } finally {
            handleCloseAddLeadModal();
        }
    };

    const handleCloseAddLeadModal = () => {
        setShowAddLeadModal(false);
        setAddLeadData({
            name: "",
            phoneNumber: "",
            companyId: "",
            assignedTo: "",
            sourceId: "",
            campaignId: "",
            remarks: ""
        });
    };


    // Define TypeScript interfaces
    interface Variable {
        fullVar: string;  // With curly braces e.g. {{name}}
        key: string;      // Without curly braces e.g. name
    }

    interface ComponentState {
        type: string;
        text: string;
        variables: Variable[];
        inputFieldCount: number;
    }

    interface InputValues {
        [key: string]: string;
    }

    const [templates, setTemplates] = useState<WhatsappTemplate[]>([]);
    const [showMediaModal, setShowMediaModal] = useState(false);
    const [mediaFormat, setMediaFormat] = useState<string | null>(null);
    const [selectedTemplate, setSelectedTemplate] = useState<WhatsappTemplate>({} as WhatsappTemplate);
    // Generate the state based on components
    const [componentsState, setComponentsState] = useState<ComponentState[]>([]);

    // State to track input values
    const [inputValues, setInputValues] = useState<InputValues>({});

    // Loading state for sending template
    const [sendLoading, setSendLoading] = useState(false);

    // Handle input change
    const handleInputChange = (type: string, key: string, value: string): void => {
        setInputValues(prev => ({
            ...prev,
            [`${type}_${key}`]: value
        }));
    };

    const handleTemplateClick = async () => {

        try {
            const user = auth.currentUser;
            if (!user) {
                console.error("User not authenticated");
                return;
            }
            const userRef = doc(db, "users", user.uid);
            const userDoc = await getDoc(userRef);
            if (!userDoc.exists()) {
                console.error("User document not found");
                return;
            }
            const userData = userDoc.data();
            const companyId = userData.companyId;
            if (!companyId) {
                console.error("Company ID not found in user document");
                return;
            }

            // Fetch message templates 
            const reponse = await whatsappTemplateMessage(companyId);

            if (reponse) {
                const templates = reponse.data;
                console.log("Templates:", templates);
                setTemplates(templates);
                setShowTemplateModal(true);
            } else {
                console.error("No templates found");
            }

        } catch (error) {
            console.error('Error fetching message templates:', error);
            throw error;
        }
    }

    // Get status badge color based on template status
    const getStatusColor = (status?: TemplateStatus) => {
        switch (status) {
            case 'APPROVED':
                return 'bg-green-100 text-green-800';
            case 'REJECTED':
                return 'bg-red-100 text-red-800';
            case 'PENDING':
            default:
                return 'bg-yellow-100 text-yellow-800';
        }
    };

    const handleCloseTemplateModal = () => {
        setShowTemplateModal(false);
        setTemplates([]);
    };

    // Handle file selection for media messages
    const handleTemplateFileSelection = async (e: React.ChangeEvent<HTMLInputElement>) => {
        // Check if a file was selected
        const file = e.target.files?.[0];
        if (!file || !selectedContact || !selectedPhoneNumberInfo || !companyId) {
            // No file selected or missing required data
            console.log("File selection canceled or missing data");
            setSelectedMediaType(null);
            return;
        }

        // Store the selected file for later sending
        setSelectedFile(file);

        let mediaType: string | null = null;

        // find the media type based on the file extension
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        if (fileExtension === 'jpg' || fileExtension === 'jpeg' || fileExtension === 'png') {
            mediaType = 'image';
        } else if (fileExtension === 'mp4' || fileExtension === 'mov') {
            mediaType = 'video';
        } else if (fileExtension === 'pdf' || fileExtension === 'docx' || fileExtension === 'txt') {
            mediaType = 'document';
        } else {
            console.error("Unsupported file type");
            setToast({
                show: true,
                message: "Unsupported file type. Please select an image, video, or document.",
                type: "error"
            });
            return;
        }

        try {
            console.log("Uploading media file:", file);
            console.log("-------------------------------------------");


            setUploadingMedia(true);

            // Get access token if not already available
            const token = accessToken || await fetchAccessToken();

            if (!token) {
                throw new Error("Could not get access token for WhatsApp API");
            }

            // Convert file to base64
            const base64Data = await fileToBase64(file);
            // Remove the data URL prefix (e.g., 'data:image/jpeg;base64,')
            const base64Content = base64Data.split(',')[1];

            // Upload media to WhatsApp
            const uploadResult = await uploadWhatsAppMediaFunction({
                accessToken: token,
                accountPhoneNumberId: selectedPhoneNumberInfo.numberId,
                mediaType: mediaType,
                mediaData: base64Content,
                mimeType: file.type,
                filename: file.name
            });

            const { success, mediaId } = uploadResult.data as {
                success: boolean;
                mediaId: string;
            };

            if (!success || !mediaId) {
                throw new Error("Failed to upload media to WhatsApp");
            }

            // Store the media ID for later sending
            setUploadedMediaId(mediaId);
            setUploadingMedia(false);

            // Show a toast indicating media is ready to send
            setToast({
                show: true,
                message: `${selectedMediaType} uploaded successfully. Add a caption if you wish, then click send.`,
                type: "success"
            });

        } catch (err) {
            console.error("Error uploading media:", err);
            setUploadingMedia(false);
            setSelectedMediaType(null);
            setSelectedFile(null);
            setUploadedMediaId(null);

            // Show error toast
            setToast({
                show: true,
                message: "Failed to upload media. Please try again.",
                type: "error"
            });
        }
    };

    // Function to process components and extract variables
    function processComponents(components: TemplateComponent[]): ComponentState[] {
        const state: ComponentState[] = [];
        const variableRegex = /{{([^{}]+)}}/g;

        components.forEach(component => {
            if (!component.text) {
                return;
            }

            const variables: Variable[] = [];
            let match: RegExpExecArray | null;
            let textCopy = component.text;

            while ((match = variableRegex.exec(textCopy)) !== null) {
                variables.push({
                    fullVar: match[0],
                    key: match[1]
                });
            }

            if (variables.length > 0) {
                state.push({
                    type: component.type,
                    text: component.text,
                    variables: variables,
                    inputFieldCount: variables.length
                });
            }
        });

        return state;
    }

    const templateFormat = (template: WhatsappTemplate) => {
        console.log("Template format:", template);
        if (template.components[0].format === 'IMAGE' || template.components[0].format === 'VIDEO' || template.components[0].format === 'DOCUMENT') {
            if (selectedFile === null) {
                setShowMediaModal(true);
                setMediaFormat(template.components[0].format);
            }
        }
        // Set selected template
        setSelectedTemplate(template);
        // Process components to extract variables
        setComponentsState(processComponents(template.components));
    }

    useEffect(() => {
        if (componentsState.length === 0 && showMediaModal === false) {
            handleSendTemplate(selectedTemplate);
        }
    }, [selectedTemplate, componentsState]);

    const mediaAcceptedFormats = (format: string | null) => {
        switch (format) {
            case 'IMAGE':
                return "image/jpeg, image/png, image/jpg"
            case 'VIDEO':
                return "video/mp4, video/mov"
            case 'DOCUMENT':
                return "application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            default:
                return undefined;
        }
    }

    const handleComponentModalClose = () => {
        setComponentsState([]);
        setInputValues({});
        setSelectedTemplate({} as WhatsappTemplate);
        setShowMediaModal(false);
        setSelectedFile(null);
        setSendLoading(false);
    }

    const handleSendTemplate = async (template: WhatsappTemplate) => {

        console.log("Sending template:", template);
        setSendLoading(true);


        if (!selectedContact || !selectedPhoneNumberInfo || !companyId) {
            console.error("Missing required data to send template");
            return;
        }

        try {
            console.log("info", selectedPhoneNumberInfo);

            setSendingMessage(true);

            // Get access token if not already available
            const token = accessToken || await fetchAccessToken();

            if (!token) {
                throw new Error("Could not get access token for WhatsApp API");
            }

            let data: any = {};

            if (template.components[0].type === 'HEADER') {
                if (template.components[0].format === 'IMAGE' || template.components[0].format === 'VIDEO' || template.components[0].format === 'DOCUMENT') {

                    console.log("Sending template with media:", template.components[0].format);
                    console.log("uploadedMediaId:", uploadedMediaId);

                    console.log("variables:", componentsState.map((component: ComponentState) => component.variables));
                    console.log("inputValues:", inputValues);

                    // Start with the header component that's always included
                    const components = [
                        {
                            type: 'header',
                            parameters: [
                                {
                                    type: template.components[0].format.toLowerCase(),
                                    [template.components[0].format.toLowerCase()]:
                                        template.components[0].format.toLowerCase() === 'document'
                                            ? { id: uploadedMediaId, filename: selectedFile?.name }
                                            : template.components[0].format.toLowerCase() === 'image' || template.components[0].format.toLowerCase() === 'video'
                                                ? { id: uploadedMediaId }
                                                : { id: uploadedMediaId }
                                }
                            ]
                        }
                    ];

                    // Only add additional components if componentsState has items
                    if (componentsState.length > 0) {
                        // Process components (body, footer, etc.)
                        const additionalComponents = ['header', 'body', 'footer']
                            .map(type => {
                                // Find variables belonging to this component type
                                const typeVariables = componentsState
                                    .filter(component => component && component.type.toLowerCase() === type)
                                    .flatMap(component => component.variables || []);

                                // Only create component if it has variables
                                if (typeVariables.length === 0) {
                                    return null;
                                }

                                return {
                                    type,
                                    parameters: typeVariables.map((variable) => ({
                                        type: 'text',
                                        // add "parameter_name" field to the object if template.parameter_format is === "NAMED" and the value is variable.fullVar without curly braces
                                        ...(template.parameter_format === 'NAMED' ? { parameter_name: variable.fullVar.replace(/{{\s*([^{}]+)\s*}}/g, '$1') } : {}),
                                        text: variable.fullVar.replace(/{{\s*([^{}]+)\s*}}/g, (match, key) => {
                                            return inputValues[`${type.toUpperCase()}_${key}`] || match;
                                        })
                                    }))
                                };
                            })
                            .filter(Boolean); // Remove null entries for component types with no variables

                        // If we have valid additional components, add the first one
                        if (additionalComponents.length > 0) {
                            if (additionalComponents[0]) {
                                components.push(additionalComponents[0]);
                            }
                        }
                    }


                    data = {
                        to: selectedContact.phoneNumber,
                        templateName: template.name,
                        templateLanguage: template.language,
                        accessToken: token,
                        accountPhoneNumberId: selectedPhoneNumberInfo.numberId,
                        companyId,
                        components: components,
                        displayFields: {
                            headerText: null,
                            body: template.components[1]?.text?.replace(/{{\s*([^{}]+)\s*}}/g, (match: any, key: any) => {
                                return inputValues[`${template.components[1]?.type}_${key}`] || match;
                            }) || '',
                            footer: template.components[2] && template.components[2].type === 'FOOTER' ? template.components[2].text || '' : ''
                        }
                    }
                } else {
                    console.log("componentsState:", componentsState);
                    console.log("variables:", componentsState.map((component: ComponentState) => component.variables));
                    console.log("inputValues:", inputValues);

                    data = {
                        to: selectedContact.phoneNumber,
                        templateName: template.name,
                        templateLanguage: template.language,
                        accessToken: token,
                        accountPhoneNumberId: selectedPhoneNumberInfo.numberId,
                        companyId,
                        // Only include components if componentsState has items
                        ...(componentsState.length > 0 ? {
                            components: ['header', 'body', 'footer']
                                .map(type => {
                                    // Find variables belonging to this component type
                                    const typeVariables = componentsState
                                        .filter(component => component && component.type.toLocaleLowerCase() === type)
                                        .flatMap(component => component.variables || []);

                                    // Only create component if it has variables
                                    if (typeVariables.length === 0) {
                                        return null;
                                    }

                                    return {
                                        type,
                                        parameters: typeVariables.map((variable: Variable) => ({
                                            type: 'text',
                                            // add "parameter_name" field to the object if template.parameter_format is === "NAMED" and the value is variable.fullVar without curly braces
                                            ...(template.parameter_format === 'NAMED' ? { parameter_name: variable.fullVar.replace(/{{\s*([^{}]+)\s*}}/g, '$1') } : {}),
                                            text: variable.fullVar.replace(/{{\s*([^{}]+)\s*}}/g, (match: any, key: any) => {
                                                return inputValues[`${type.toLocaleUpperCase()}_${key}`] || match;
                                            })
                                        }))
                                    };
                                })
                                .filter(Boolean) // Remove null entries for component types with no variables
                        } : {}),
                        displayFields: {
                            headerText: template.components[0]?.text?.replace(/{{\s*([^{}]+)\s*}}/g, (match: any, key: any) => {
                                return inputValues[`${template.components[0]?.type}_${key}`] || match;
                            }) || '',
                            body: template.components[1]?.text?.replace(/{{\s*([^{}]+)\s*}}/g, (match: any, key: any) => {
                                return inputValues[`${template.components[1]?.type}_${key}`] || match;
                            }) || '',
                            footer: template.components[2] && template.components[2].type === 'FOOTER' ? template.components[2].text || '' : ''
                        }
                    }
                }
            }

            console.log("Data to send:", data);


            // Send the message via the Cloud Function
            const result = await sendWhatsAppTemplateMessage({
                to: data?.to,
                templateName: data?.templateName,
                templateLanguage: data?.templateLanguage,
                components: data?.components,
                accessToken: data?.accessToken,
                accountPhoneNumberId: data?.accountPhoneNumberId,
                companyId: data?.companyId,
                displayFields: data?.displayFields
            });

            const { success } = result.data as {
                success: boolean;
                messageId: string;
                cleanId: string;
            };

            if (!success) {
                throw new Error("Failed to send message through WhatsApp API");
            }

            // No need to update the database here as the Cloud Function handles it
            // Just clear the message input and show success in the UI
            setNewMessage("");

            // The UI will be updated automatically via the Realtime Database listeners
            // when the new message is stored by the Cloud Function

            // Scroll to the bottom of the chat
            setTimeout(() => {
                messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            }, 100);

            // Show success message
            setToast({
                show: true,
                message: "Template sent successfully",
                type: "success"
            });

            // Close the template modal
            handleCloseTemplateModal();
            handleComponentModalClose();
            setShowMediaModal(false);
            setSelectedFile(null);
            setUploadedMediaId(null);
            setSelectedMediaType(null);
            setInputValues({});
            setComponentsState([]);
            setSelectedTemplate({} as WhatsappTemplate);

        } catch (err) {
            console.error("Error sending template message:", err);
        } finally {
            setSendingMessage(false);
            setSendLoading(false);
        }
    }



    return (
        <div className="min-h-screen bg-gray-200 flex flex-col">
            {toast && toast.show && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={hideToast}
                />
            )}
            <div className="fixed top-16 left-0 h-full z-10">
                <SideNavbar />
            </div>

            <div className={`${isCollapsed ? 'ml-[70px]' : 'ml-[290px]'} fixed top-16 left-0 right-0 bottom-0 px-6 transition-all duration-300 flex-1 flex flex-col overflow-hidden`} style={{ top: '140px' }}>
                {loading && phoneNumbers.length === 0 ? (
                    <div className="bg-white p-6 rounded-xl shadow-md w-full flex justify-center items-center h-40">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
                    </div>
                ) : error ? (
                    <div className="bg-white p-6 rounded-xl shadow-md w-full">
                        <div className="text-red-500 p-4 border border-red-300 bg-red-50 rounded-md">
                            {error}
                        </div>
                    </div>
                ) : phoneNumbers.length === 0 ? (
                    <div className="bg-white p-6 rounded-xl shadow-md w-full">
                        <div className="text-gray-500 p-4 border border-gray-300 bg-gray-50 rounded-md">
                            No WhatsApp phone numbers found for your company.
                        </div>
                    </div>
                ) : (
                    <div className="bg-[#f0f2f5] rounded-3xl shadow-lg w-full overflow-hidden flex flex-col h-[calc(100vh-150px)]">
                        {/* WhatsApp Header - Gradient style similar to WhatsApp */}
                        <div className="bg-gradient-to-r from-[#00a884] to-[#00a884] px-4 py-3 text-white flex-shrink-0 z-10">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                    </svg>
                                    <h1 className="text-xl font-semibold">WhatsApp Business</h1>
                                </div>
                                <div className="text-sm flex flex-col items-end">
                                    <div>{phoneNumbers.length} WhatsApp accounts</div>
                                    {/* {whatsappAccessToken && whatsappAccessToken.accountExpiration && (
                                        <span className="text-xs text-gray-300">
                                            Expires: {whatsappAccessToken.accountExpiration?.toDate ?
                                                whatsappAccessToken.accountExpiration.toDate().toLocaleString() :
                                                new Date(whatsappAccessToken.accountExpiration).toLocaleString()}
                                        </span>
                                    )} */}
                                </div>
                            </div>
                        </div>

                        {/* Chat Container */}
                        <div className="flex flex-1 overflow-hidden" style={{ height: 'calc(100% - 48px)' }}>
                            {/* Contacts List */}
                            <div className="w-1/3 border-r border-gray-200 flex flex-col h-full overflow-hidden bg-white z-20">
                                <div className="p-2 border-b border-gray-200 bg-[#f0f2f5]">
                                    <div className="relative">
                                        <input
                                            type="text"
                                            placeholder="Search contacts..."
                                            className="w-full py-2 px-10 bg-white rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                        <div className="absolute left-3 top-2 text-gray-400">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex-grow overflow-y-auto" style={{ maxHeight: 'calc(100% - 56px)' }}>
                                    {loading ? (
                                        <div className="flex justify-center items-center h-full">
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
                                        </div>
                                    ) : contacts.length === 0 ? (
                                        <div className="flex justify-center items-center h-full text-gray-600">
                                            No contacts found for any of your WhatsApp numbers.
                                        </div>
                                    ) : (
                                        <div className="divide-y divide-gray-100">
                                            {contacts
                                                .filter(contact => contact.contactName.toLowerCase().includes(searchTerm.toLowerCase()))
                                                .sort((a, b) => {
                                                    // First sort by unread count (descending)
                                                    if ((a.unreadCount || 0) !== (b.unreadCount || 0)) {
                                                        return (b.unreadCount || 0) - (a.unreadCount || 0);
                                                    }

                                                    // Then sort by timestamp (most recent first)
                                                    const aTime = a.lastMessage ? parseInt(a.lastMessage.timestamp) : 0;
                                                    const bTime = b.lastMessage ? parseInt(b.lastMessage.timestamp) : 0;
                                                    return bTime - aTime;
                                                })
                                                .map(contact => (
                                                    <div
                                                        key={`${contact.phoneNumberId}-${contact.phoneNumber}`}
                                                        className={`p-3 hover:bg-gray-50 cursor-pointer transition-colors duration-150 flex items-center ${selectedContact?.phoneNumber === contact.phoneNumber
                                                            ? 'bg-[#f0f2f5]'
                                                            : (contact.lastMessage?.direction === 'incoming' || (!contact.lastMessage?.direction && contact.lastMessage?.from !== contact.phoneNumberId))
                                                                ? contact.unreadCount ? 'bg-[#e7f7ef]' : 'bg-white'
                                                                : 'bg-white'
                                                            }`}
                                                        onClick={() => handleContactSelect(contact)}
                                                    >
                                                        <div className="flex items-center space-x-3 w-full">
                                                            <div className="flex-shrink-0 h-12 w-12 rounded-full bg-[#DFE5E7] flex items-center justify-center text-gray-600 shadow-sm">
                                                                {contact.contactName.charAt(0).toUpperCase()}
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex justify-between">
                                                                    <p className={`text-sm font-medium ${contact.unreadCount ? 'text-black font-semibold' : 'text-gray-800'} truncate`}>
                                                                        {contact.contactName}
                                                                    </p>
                                                                    <p className="text-xs text-gray-500">
                                                                        {formatMessageTime(contact.lastMessage?.timestamp)}
                                                                    </p>
                                                                </div>
                                                                <div className="flex justify-between mt-1">
                                                                    <p className={`text-xs ${contact.unreadCount ? 'text-black' : 'text-gray-500'} truncate max-w-[120px]`}>
                                                                        {getLastMessagePreview(contact.lastMessage)}
                                                                    </p>
                                                                    {contact.unreadCount ? (
                                                                        <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-[#25D366] text-white text-xs font-medium">
                                                                            {contact.unreadCount}
                                                                        </span>
                                                                    ) : contact.isOutsideServiceWindow ? (
                                                                        <span className="inline-flex h-2 w-2 rounded-full bg-yellow-400"></span>
                                                                    ) : null}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Chat Area */}
                            <div className="flex-1 flex flex-col h-full bg-[#efeae2] relative overflow-hidden">
                                {selectedContact ? (
                                    <>
                                        {/* Selected contact header */}
                                        <div className="p-3 bg-[#f0f2f5] border-b border-gray-200 flex justify-between items-center flex-shrink-0 z-20">
                                            <div className="flex items-center">
                                                <div className="h-10 w-10 rounded-full bg-[#DFE5E7] flex items-center justify-center text-gray-600 mr-3">
                                                    {selectedContact.contactName.charAt(0).toUpperCase()}
                                                </div>
                                                <div>
                                                    <div className="text-sm flex items-end gap-1">
                                                        <h2 className="text-gray-900 font-medium text-base">{selectedContact.contactName}</h2>
                                                        {checkNumberLoading ? (
                                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                                                        ) : checkNumberExists === false ? (
                                                            <button className='text-xs font-medium bg-[#E6F7FF] text-[#777873] py-[2px] px-2 rounded' onClick={handleAddLeadClick} >
                                                                Add Lead
                                                            </button>
                                                        ) : <div className='text-xs font-medium bg-[#E6F7FF] text-[#54be34] py-[2px] px-2 rounded' >
                                                            Existing Lead
                                                        </div>
                                                        }
                                                    </div>
                                                    <p className="text-xs text-gray-500">{selectedContact.phoneNumber} • via {selectedContact.displayPhoneNumber}</p>
                                                </div>
                                            </div>
                                            <div className="text-sm flex flex-col items-end">
                                                {selectedContact.lastMessage?.timestamp && (
                                                    <p className="text-xs text-gray-500">{formatMessageTime(selectedContact.lastMessage.timestamp)}</p>
                                                )}
                                            </div>
                                        </div>

                                        {/* Messages display */}
                                        <div
                                            ref={chatContainerRef}
                                            className="flex-1 overflow-y-auto py-3 px-4 z-10 bg-[#efeae2]"
                                        >
                                            {loadingMessages ? (
                                                <div className="flex justify-center items-center h-full">
                                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                                                </div>
                                            ) : messages.length === 0 ? (
                                                <div className="flex flex-col justify-center items-center h-full text-gray-600">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-blue-500 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                    </svg>
                                                    <p className="font-medium mb-1">No messages yet</p>
                                                    <p className="text-sm text-gray-500">Type a message below to get started</p>
                                                </div>
                                            ) : (
                                                <div className="space-y-3">
                                                    {messages.map((message, index) => (
                                                        <div
                                                            key={index}
                                                            className={`flex mb-2 ${message.direction === 'outgoing' ? 'justify-end' : 'justify-start'}`}
                                                        >
                                                            <div
                                                                className={`relative px-3 py-2 rounded-lg shadow-sm max-w-xs md:max-w-md ${message.direction === 'outgoing'
                                                                    ? 'bg-[#d9fdd3] text-gray-800'
                                                                    : 'bg-white text-gray-800'
                                                                    }`}
                                                                style={{
                                                                    borderTopRightRadius: message.direction === 'outgoing' ? '0' : '8px',
                                                                    borderTopLeftRadius: message.direction === 'outgoing' ? '8px' : '0',
                                                                }}
                                                            >
                                                                {/* Text Message */}
                                                                {message.type === 'text' && (
                                                                    <>
                                                                        <div className="text-sm">{message.text?.body || message.message}</div>
                                                                        <div className="text-right mt-1">
                                                                            <span className="text-xs text-gray-500">
                                                                                {formatMessageTime(message.timestamp)}
                                                                            </span>
                                                                        </div>
                                                                    </>
                                                                )}

                                                                {/* Image Message */}
                                                                {message.type === 'image' && (
                                                                    <>
                                                                        <div className="rounded-md overflow-hidden">
                                                                            {message.image?.url ? (
                                                                                <img
                                                                                    src={message.image.url}
                                                                                    alt="Image"
                                                                                    className="max-h-64 object-contain cursor-pointer"
                                                                                    onClick={() => window.open(message.image?.url, '_blank')}
                                                                                />
                                                                            ) : (
                                                                                <div
                                                                                    className="bg-gray-200 h-40 w-40 flex flex-col items-center justify-center cursor-pointer"
                                                                                    onClick={() => message.image?.id && handleMediaDownload(message.image.id, 'image')}
                                                                                >
                                                                                    <span className="text-3xl mb-2">🖼️</span>
                                                                                    <span className="text-sm font-medium">Download Image</span>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        {message.image?.caption && (
                                                                            <p className="text-sm mt-2 border-t border-gray-200 pt-2">{message.image?.caption}</p>
                                                                        )}
                                                                        <div className="text-right mt-1">
                                                                            <span className="text-xs text-gray-500">
                                                                                {formatMessageTime(message.timestamp)}
                                                                            </span>
                                                                        </div>
                                                                    </>
                                                                )}

                                                                {/* Video Message */}
                                                                {message.type === 'video' && (
                                                                    <>
                                                                        <div className="rounded-md overflow-hidden">
                                                                            {message.video?.url ? (
                                                                                <video
                                                                                    controls
                                                                                    className="max-h-64 max-w-full"
                                                                                    onClick={(e) => e.stopPropagation()}
                                                                                >
                                                                                    <source src={message.video.url} type={message.video.mime_type} />
                                                                                    Your browser does not support the video tag.
                                                                                </video>
                                                                            ) : (
                                                                                <div
                                                                                    className="bg-gray-200 h-40 w-40 flex flex-col items-center justify-center cursor-pointer"
                                                                                    onClick={() => message.video?.id && handleMediaDownload(message.video.id, 'video')}
                                                                                >
                                                                                    <span className="text-3xl mb-2">🎬</span>
                                                                                    <span className="text-sm font-medium">Download Video</span>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        {message.video?.caption && (
                                                                            <p className="text-sm mt-2 border-t border-gray-200 pt-2">{message.video?.caption}</p>
                                                                        )}
                                                                        <div className="text-right mt-1">
                                                                            <span className="text-xs text-gray-500">
                                                                                {formatMessageTime(message.timestamp)}
                                                                            </span>
                                                                        </div>
                                                                    </>
                                                                )}

                                                                {/* Document Message */}
                                                                {message.type === 'document' && (
                                                                    <>
                                                                        <div
                                                                            className="rounded-md overflow-hidden bg-gray-100 p-3 border border-gray-200 cursor-pointer"
                                                                            onClick={() => {
                                                                                if (message.document?.url) {
                                                                                    window.open(message.document.url, '_blank');
                                                                                } else if (message.document?.id) {
                                                                                    handleMediaDownload(message.document.id, 'document', message.document.filename);
                                                                                }
                                                                            }}
                                                                        >
                                                                            <div className="flex items-center">
                                                                                <span className="text-2xl mr-3">📄</span>
                                                                                <div className="flex-1">
                                                                                    <p className="text-sm font-medium truncate">
                                                                                        {message.document?.filename || 'Document'}
                                                                                    </p>
                                                                                    <p className="text-xs text-gray-500">
                                                                                        {message.document?.mime_type || ''}
                                                                                    </p>
                                                                                    {!message.document?.url && message.document?.id && (
                                                                                        <p className="text-xs text-blue-500 mt-1">Click to download</p>
                                                                                    )}
                                                                                </div>
                                                                                {message.document?.url && (
                                                                                    <a
                                                                                        href={message.document.url}
                                                                                        download={message.document.filename}
                                                                                        target="_blank"
                                                                                        rel="noopener noreferrer"
                                                                                        className="bg-blue-500 hover:bg-blue-600 text-white p-1 rounded-full ml-2"
                                                                                        onClick={(e) => e.stopPropagation()}
                                                                                    >
                                                                                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 01-3 3h-10a2 2 0 01-2-2V6a2 2 0 012-2h12a2 2 0 012 2v10a2 2 0 01-2 2h-3.5" />
                                                                                        </svg>
                                                                                    </a>
                                                                                )}
                                                                            </div>
                                                                            {message.document?.caption && (
                                                                                <p className="text-sm mt-2 border-t border-gray-200 pt-2">{message.document.caption}</p>
                                                                            )}
                                                                        </div>
                                                                        <div className="text-right mt-1">
                                                                            <span className="text-xs text-gray-500">
                                                                                {formatMessageTime(message.timestamp)}
                                                                            </span>
                                                                        </div>
                                                                    </>
                                                                )}

                                                                {/* Location Message */}
                                                                {message.type === 'location' && message.location && (
                                                                    <>
                                                                        <div
                                                                            className="rounded-md overflow-hidden cursor-pointer"
                                                                            onClick={() => {
                                                                                if (message.location) {
                                                                                    const { latitude, longitude } = message.location;
                                                                                    window.open(`https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`, '_blank');
                                                                                }
                                                                            }}
                                                                        >
                                                                            <div className="bg-gray-100 p-3 border border-gray-200">
                                                                                <div className="flex items-center">
                                                                                    <span className="text-2xl mr-3">📍</span>
                                                                                    <div>
                                                                                        <p className="text-sm font-medium">
                                                                                            {message.location.name || 'Location'}
                                                                                        </p>
                                                                                        {message.location.address && (
                                                                                            <p className="text-xs text-gray-500">{message.location.address}</p>
                                                                                        )}
                                                                                        <p className="text-xs text-blue-500 mt-1">
                                                                                            Open in Google Maps
                                                                                        </p>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        <div className="text-right mt-1">
                                                                            <span className="text-xs text-gray-500">
                                                                                {formatMessageTime(message.timestamp)}
                                                                            </span>
                                                                        </div>
                                                                    </>
                                                                )}

                                                                {/* Audio Message */}
                                                                {message.type === 'audio' && (
                                                                    <>
                                                                        <div className="rounded-md overflow-hidden">
                                                                            {message.audio?.url ? (
                                                                                <audio
                                                                                    controls
                                                                                    className="w-full"
                                                                                    onClick={(e) => e.stopPropagation()}
                                                                                >
                                                                                    <source src={message.audio.url} type={message.audio.mime_type} />
                                                                                    Your browser does not support the audio element.
                                                                                </audio>
                                                                            ) : message.audio?.id && audioMediaUrls[message.audio.id] ? (
                                                                                <audio
                                                                                    controls
                                                                                    className="w-full"
                                                                                    onClick={(e) => e.stopPropagation()}
                                                                                >
                                                                                    <source src={audioMediaUrls[message.audio.id]} type={message.audio.mime_type || 'audio/mpeg'} />
                                                                                    Your browser does not support the audio element.
                                                                                </audio>
                                                                            ) : (
                                                                                <div
                                                                                    className="bg-gray-200 p-3 flex flex-col items-center justify-center cursor-pointer"
                                                                                    onClick={() => message.audio?.id && handleMediaDownload(message.audio.id, 'audio', 'audio_message.mp3')}
                                                                                >
                                                                                    <span className="text-3xl mb-2">🔊</span>
                                                                                    <span className="text-sm font-medium">Listen to Audio</span>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        <div className="text-right mt-1">
                                                                            <span className="text-xs text-gray-500">
                                                                                {formatMessageTime(message.timestamp)}
                                                                            </span>
                                                                        </div>
                                                                    </>
                                                                )}

                                                                {/* Template Message */}
                                                                {message.type === 'template' && (
                                                                    <div className="rounded-md overflow-hidden bg-gray-100 p-3 border border-gray-200 space-y-3 max-w-xs">
                                                                        {/* Header */}
                                                                        {message.header && (
                                                                            <div className="text-sm font-semibold text-gray-700">
                                                                                {message.header}
                                                                            </div>
                                                                        )}

                                                                        {message.document && (
                                                                            <div
                                                                                className="rounded-md overflow-hidden bg-gray-100 p-3 border border-gray-200 cursor-pointer"
                                                                                onClick={() => {
                                                                                    if (message.document?.url) {
                                                                                        window.open(message.document.url, '_blank');
                                                                                    } else if (message.document?.id) {
                                                                                        handleMediaDownload(message.document.id, 'document', message.document.filename);
                                                                                    }
                                                                                }}
                                                                            >
                                                                                <div className="flex items-center">
                                                                                    <span className="text-2xl mr-3">📄</span>
                                                                                    <div className="flex-1">
                                                                                        <p className="text-sm font-medium truncate">
                                                                                            {message.document?.filename || 'Document'}
                                                                                        </p>
                                                                                        <p className="text-xs text-gray-500">
                                                                                            {message.document?.mime_type || ''}
                                                                                        </p>
                                                                                        {!message.document?.url && message.document?.id && (
                                                                                            <p className="text-xs text-blue-500 mt-1">Click to download</p>
                                                                                        )}
                                                                                    </div>
                                                                                    {message.document?.url && (
                                                                                        <a
                                                                                            href={message.document.url}
                                                                                            download={message.document.filename}
                                                                                            target="_blank"
                                                                                            rel="noopener noreferrer"
                                                                                            className="bg-blue-500 hover:bg-blue-600 text-white p-1 rounded-full ml-2"
                                                                                            onClick={(e) => e.stopPropagation()}
                                                                                        >
                                                                                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 01-3 3h-10a2 2 0 01-2-2V6a2 2 0 012-2h12a2 2 0 012 2v10a2 2 0 01-2 2h-3.5" />
                                                                                            </svg>
                                                                                        </a>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        )}

                                                                        {message?.image && (
                                                                            <div className="rounded-md overflow-hidden">
                                                                                {message.image.url ? (
                                                                                    <img
                                                                                        src={message.image.url}
                                                                                        alt="Image"
                                                                                        className="max-h-64 object-contain cursor-pointer"
                                                                                    />
                                                                                ) : (
                                                                                    <div
                                                                                        className="bg-gray-200 h-40 w-40 flex flex-col items-center justify-center cursor-pointer"
                                                                                        onClick={() => message.image?.id && handleMediaDownload(message.image.id, 'image')}
                                                                                    >
                                                                                        <span className="text-3xl mb-2">🖼️</span>
                                                                                        <span className="text-sm font-medium">Download Image</span>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        )}

                                                                        {message?.video && (
                                                                            <div className="rounded-md overflow-hidden">
                                                                                {message.video.url ? (
                                                                                    <video
                                                                                        controls
                                                                                        className="max-h-64 max-w-full"
                                                                                        onClick={(e) => e.stopPropagation()}
                                                                                    >
                                                                                        <source src={message.video.url} type={message.video.mime_type} />
                                                                                        Your browser does not support the video tag.
                                                                                    </video>
                                                                                ) : (
                                                                                    <div
                                                                                        className="bg-gray-200 h-40 w-40 flex flex-col items-center justify-center cursor-pointer"
                                                                                        onClick={() => message.video?.id && handleMediaDownload(message.video.id, 'video')}
                                                                                    >
                                                                                        <span className="text-3xl mb-2">🎬</span>
                                                                                        <span className="text-sm font-medium">Download Video</span>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        )}

                                                                        {/* Body */}
                                                                        {message.body && (
                                                                            <div className="text-sm text-gray-800 whitespace-pre-line">
                                                                                {message.body}
                                                                            </div>
                                                                        )}

                                                                        {/* Footer */}
                                                                        {message.footer && (
                                                                            <div className="text-xs text-gray-500 border-t pt-2 mt-2">
                                                                                {message.footer}
                                                                            </div>
                                                                        )}

                                                                        {/* Timestamp */}
                                                                        <div className="text-right mt-1">
                                                                            <span className="text-xs text-gray-400">
                                                                                {formatMessageTime(message.timestamp)}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                )}


                                                                {/* Message Status */}

                                                                <div className="text-xs text-gray-500 text-right mt-1">
                                                                    {message.direction === 'outgoing' && (
                                                                        <span className="ml-1">
                                                                            {message.status === 'delivered' ? (
                                                                                <span className="text-blue-500 font-semibold">✓✓</span>
                                                                            ) : message.status === 'sent' ? (
                                                                                <span>✓✓</span>
                                                                            ) : message.status === 'failed' ? (
                                                                                <span className="text-red-500">✗</span>
                                                                            ) : message.status === 'read' ? (
                                                                                <span className="text-green-500 font-semibold">✓✓</span>
                                                                            ) : (
                                                                                <span>✓</span>
                                                                            )}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                    <div ref={messagesEndRef} />
                                                </div>
                                            )}
                                        </div>

                                        {/* Message Input - Fixed at bottom */}
                                        <div className="bg-[#f0f2f5] border-t border-gray-200 p-2">
                                            {expired && (
                                                <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-2 mb-2 text-sm">
                                                    <p>WhatsApp access token has expired. Please contact an admin to renew the token.</p>
                                                </div>
                                            )}

                                            {selectedContact?.isOutsideServiceWindow && (
                                                <div className="bg-yellow-50 border-l-4 border-yellow-500 text-yellow-700 p-2 mb-2 text-sm">
                                                    <p>
                                                        This contact is outside the 24-hour service window. You can only reply to messages initiated by the customer within the last 24 hours.
                                                    </p>
                                                </div>
                                            )}

                                            {/* Media Preview */}
                                            {selectedMediaType && (
                                                <div className="bg-white rounded-lg p-3 mb-2 border border-gray-200">
                                                    <div className="flex justify-between items-center mb-2">
                                                        <h3 className="font-medium text-sm">
                                                            {selectedMediaType === 'image' ? 'Send Image' :
                                                                selectedMediaType === 'video' ? 'Send Video' :
                                                                    'Send Document'}
                                                        </h3>
                                                        <button
                                                            onClick={handleCancelMedia}
                                                            className="text-gray-500 hover:text-gray-700"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                            </svg>
                                                        </button>
                                                    </div>

                                                    <div className="flex flex-col space-y-3">
                                                        {!uploadedMediaId && !mediaPreviewUrl && !uploadingMedia && (
                                                            <div
                                                                className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:bg-gray-50"
                                                                onClick={() => fileInputRef.current?.click()}
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                                                </svg>
                                                                <p className="mt-1 text-sm text-gray-500">Click to select or drop your file here</p>
                                                            </div>
                                                        )}

                                                        {uploadingMedia && (
                                                            <div className="flex justify-center items-center p-4">
                                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500 mr-2"></div>
                                                                <p className="text-sm text-gray-600">Uploading media...</p>
                                                            </div>
                                                        )}

                                                        {/* Media Preview */}
                                                        {mediaPreviewUrl && (
                                                            <div className="flex items-center">
                                                                {selectedMediaType === 'image' && mediaPreviewUrl ? (
                                                                    <div className="relative h-24 w-24 bg-gray-200 rounded-md overflow-hidden">
                                                                        <img
                                                                            src={mediaPreviewUrl}
                                                                            alt="Preview"
                                                                            className="h-full w-full object-cover"
                                                                        />
                                                                    </div>
                                                                ) : selectedMediaType === 'video' && mediaPreviewUrl ? (
                                                                    <div className="relative h-12 w-12 bg-gray-200 rounded-md flex items-center justify-center">
                                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                                        </svg>
                                                                    </div>
                                                                ) : selectedMediaType === 'document' ? (
                                                                    <div className="relative h-12 w-12 bg-gray-200 rounded-md flex items-center justify-center">
                                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                                        </svg>
                                                                    </div>
                                                                ) : null}

                                                                <div className="ml-3 flex-1">
                                                                    <p className="text-sm font-medium text-gray-700">
                                                                        {selectedFile?.name || 'Selected file'}
                                                                    </p>
                                                                    <p className="text-xs text-gray-500">
                                                                        {selectedFile?.size ? `${Math.round(selectedFile.size / 1024)} KB` : ''}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Basic Message Input Bar - Simplified */}
                                            <div className="flex items-center bg-white rounded-3xl p-2">
                                                {/* Hidden file input for media upload */}
                                                <input
                                                    type="file"
                                                    ref={fileInputRef}
                                                    className="hidden"
                                                    accept={
                                                        selectedMediaType === 'image' ? 'image/*' :
                                                            selectedMediaType === 'video' ? 'video/*' :
                                                                selectedMediaType === 'document' ? '.pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx' :
                                                                    ''
                                                    }
                                                    onChange={handleFileSelection}
                                                />

                                                {/* Attachment Button */}
                                                <button
                                                    className={`p-2 mr-2 rounded-full hover:bg-gray-100 text-gray-500 ${selectedMediaType ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                    onClick={() => !selectedMediaType && setShowMediaOptions(!showMediaOptions)}
                                                    disabled={sendingMessage || uploadingMedia || selectedContact?.isOutsideServiceWindow || expired || !!selectedMediaType}
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                                    </svg>
                                                </button>

                                                {/* Media Options Dropdown */}
                                                {showMediaOptions && (
                                                    <div className="absolute bottom-16 left-4 bg-white rounded-lg shadow-lg w-40 py-2 z-50">
                                                        <button
                                                            className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center text-gray-700 text-sm"
                                                            onClick={() => handleMediaOptionClick('image')}
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                            </svg>
                                                            Image
                                                        </button>
                                                        <button
                                                            className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center text-gray-700 text-sm"
                                                            onClick={() => handleMediaOptionClick('video')}
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                            </svg>
                                                            Video
                                                        </button>
                                                        <button
                                                            className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center text-gray-700 text-sm"
                                                            onClick={() => handleMediaOptionClick('document')}
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                            </svg>
                                                            Document
                                                        </button>
                                                    </div>
                                                )}

                                                {/* Basic Text Input */}
                                                <input
                                                    type="text"
                                                    className="flex-1 py-2 px-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                                                    placeholder={getInputPlaceholder()}
                                                    value={newMessage}
                                                    onChange={(e) => setNewMessage(e.target.value)}
                                                    onKeyDown={handleKeyDown}
                                                    disabled={sendingMessage || uploadingMedia || selectedContact?.isOutsideServiceWindow || expired}
                                                />

                                                {/* Template Button */}
                                                <button
                                                    className={`p-2 ml-2 rounded-full hover:bg-gray-100 text-gray-500}`}
                                                    onClick={handleTemplateClick}
                                                // disabled={sendingMessage || uploadingMedia || selectedContact?.isOutsideServiceWindow || expired}
                                                >
                                                    <img src={`${process.env.PUBLIC_URL}/images/desktop/template-green.svg`} alt="Template" className="h-6 w-6" />
                                                </button>

                                                {/* Send Button */}
                                                <button
                                                    className={`p-2 ml-2 rounded-full ${(!isSendingDisabled() && (newMessage.trim() || (selectedMediaType && uploadedMediaId))) ? 'bg-[#25D366] text-white hover:bg-[#20bd5a]' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
                                                    onClick={handleSendClick}
                                                    disabled={isSendingDisabled() || (!newMessage.trim() && !(selectedMediaType && uploadedMediaId))}
                                                >
                                                    {uploadingMedia || sendingMessage ? (
                                                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                    ) : (
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                                        </svg>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    // Show a message to select a contact when no contact is selected
                                    <div className="flex flex-col justify-center items-center h-full text-gray-600 bg-[#f0f2f5] p-6">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                        </svg>
                                        <h2 className="text-xl font-medium mb-2">Welcome to WhatsApp Business</h2>
                                        <p className="text-sm max-w-md text-center mb-4">Select a contact from the list to view your conversation and start messaging.</p>
                                        <div className="flex items-center text-sm text-gray-500">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            <span>Messages are end-to-end encrypted</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {showAddLeadModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-3xl w-[500px] max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-xl font-medium">Add Lead</h3>
                                <button onClick={() => handleCloseAddLeadModal()} className="text-gray-500 hover:text-gray-700">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            <div className="mb-6">
                                <label className="block text-sm text-gray-600 mb-2">Name</label>
                                <input
                                    type="text"
                                    placeholder="Type to follow up message..."
                                    value={addLeadData?.name}
                                    onChange={(e) => handleChangeName(e)}
                                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500`}
                                />
                            </div>

                            <div className="mb-2">
                                <label className="block text-sm text-gray-600 mb-2">Phone</label>
                                <input
                                    type="text"
                                    placeholder="Type to follow up time..."
                                    value={addLeadData?.phoneNumber}
                                    onChange={(e) => handleChangePhoneNumber(e)}
                                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500`}
                                />
                            </div>


                            <div className="mb-6">
                                <label className="block text-sm text-gray-600 mb-2">Source</label>
                                <select
                                    value={addLeadData.sourceId || ''}
                                    onChange={(e) => {
                                        // Update the sourceId in your state
                                        setAddLeadData({
                                            ...addLeadData,
                                            sourceId: e.target.value
                                        });
                                    }}
                                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                                >
                                    <option value="">Select Source</option>
                                    {sources?.map((src: any) => (
                                        <option key={src.id} value={src.id} className="text-sm">
                                            {src.source}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="mb-6">
                                <label className="block text-sm text-gray-600 mb-2">Campaign</label>
                                <select
                                    value={addLeadData.campaignId || ''}
                                    onChange={(e) => {
                                        // Update the campaignId in your state
                                        setAddLeadData({
                                            ...addLeadData,
                                            campaignId: e.target.value
                                        });
                                    }}
                                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                                >
                                    <option value="">Select Campaign</option>
                                    {campaigns?.map((camp: any) => (
                                        <option key={camp.id} value={camp.id} className="text-sm">
                                            {camp.campaign}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="mb-2">
                                <label className="block text-sm text-gray-600 mb-2">Remarks</label>
                                <input
                                    type="text"
                                    placeholder="Type to follow up time..."
                                    value={addLeadData?.remarks}
                                    onChange={(e) => {
                                        // Update the remark in your state
                                        setAddLeadData({
                                            ...addLeadData,
                                            remarks: e.target.value
                                        });
                                    }}
                                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500`}
                                />
                            </div>
                            <div className="flex ml-1 mb-5">
                                <button
                                    type="button"
                                    className={`text-xs font-semibold text-gray-700 bg-teal-200 px-2 py-1 rounded-md mr-2`}
                                >
                                    AI Remarks <span className="font-normal">(coming soon)</span>
                                </button>
                            </div>

                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => handleCloseAddLeadModal()}
                                    className="px-6 py-2 text-gray-700 border border-gray-300 rounded-full hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleAddLeadSubmit}
                                    className={`px-6 py-2 text-white rounded-full bg-cyan-600 hover:bg-cyan-700`}
                                >
                                    Add Lead
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showTemplateModal && (
                // < WhatsappBusinessCloudTemplates />
                <div className="fixed inset-0 bg-gray-900 bg-opacity-80 flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col overflow-hidden">
                        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 1rem)' }}>
                            <div className="p-6">
                                <div className="flex justify-between items-center mb-6">
                                    <h3 className="text-xl font-medium">Templates</h3>
                                    <button
                                        onClick={() => handleCloseTemplateModal()}
                                        className="text-gray-500 hover:text-gray-700">
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>

                                <div className="mb-6">
                                    {templates.length > 0 ? (
                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                            {templates.map((template) => (
                                                <div key={template.id} className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden">
                                                    {/* Card Header */}
                                                    <div className="bg-gray-50 border-b border-gray-200 px-5 py-4 flex justify-between items-center">
                                                        <div className="flex items-center space-x-3">
                                                            <div className="bg-blue-100 rounded-full p-2">
                                                                <img src={`${process.env.PUBLIC_URL}/images/desktop/template-DBlue.svg`} alt="Template" className="h-6 w-6" />
                                                            </div>
                                                            <h3 className="font-semibold text-gray-900 tracking-tight" title={template.name.split('_').join(' ')}>{template.name.split('_').slice(0, 5).join('_') + '...'}</h3>
                                                        </div>
                                                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(template.status)}`}>
                                                            {template.status || 'PENDING'}
                                                        </span>
                                                    </div>

                                                    {/* Card Content */}
                                                    <div className="p-5">
                                                        <div className="flex flex-wrap gap-3 mb-4">
                                                            <div className={`px-3 py-1.5 rounded-full text-xs font-medium ${template.category === 'UTILITY' ? 'bg-blue-100 text-blue-800' : template.category === 'MARKETING' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                                                <span className="inline-flex items-center">
                                                                    {template.category === 'UTILITY' && (
                                                                        <svg className="mr-1 h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                                                        </svg>
                                                                    )}
                                                                    {template.category === 'MARKETING' && (
                                                                        <svg className="mr-1 h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                                                                        </svg>
                                                                    )}
                                                                    {template.category === 'AUTHENTICATION' && (
                                                                        <svg className="mr-1 h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                                        </svg>
                                                                    )}
                                                                    {template.category}
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                                                <span className="inline-block w-3 h-3 rounded-full bg-indigo-600 mr-1.5 flex-shrink-0"></span>
                                                                {template.language}
                                                            </div>
                                                        </div>

                                                        {/* Template Preview */}
                                                        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-5 border border-gray-200 mt-4 shadow-sm">
                                                            <div className="flex items-center mb-4">
                                                                <svg className="h-5 w-5 text-purple-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 16l2.879-2.879m0 0a3 3 0 104.243-4.242 3 3 0 00-4.243 4.242zM21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                                </svg>
                                                                <h4 className="text-sm font-semibold text-gray-700 tracking-wide">Template Preview</h4>
                                                            </div>
                                                            <div className="space-y-3 max-w-xs mx-auto bg-white rounded-lg p-4 border border-gray-300 shadow-md relative overflow-hidden">
                                                                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-400 via-blue-500 to-purple-500"></div>
                                                                {template.components?.map((component, index) => {
                                                                    if (component.type === 'HEADER') {
                                                                        if (component.format === 'TEXT' && component.text) {
                                                                            return (
                                                                                <div key={index} className="font-medium text-gray-900 pb-2 border-b border-gray-100">{component.text}</div>
                                                                            );
                                                                        } else if (component.format === 'IMAGE') {
                                                                            return (
                                                                                <div key={index} className="bg-gradient-to-r from-blue-50 to-indigo-50 h-40 rounded-lg flex flex-col items-center justify-center border border-blue-100 overflow-hidden">
                                                                                    <svg className="h-12 w-12 text-blue-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h8a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                                                    </svg>
                                                                                    <span className="text-xs text-blue-500 font-medium">Image</span>
                                                                                </div>
                                                                            );
                                                                        } else if (component.format === 'VIDEO') {
                                                                            return (
                                                                                <div key={index} className="bg-gradient-to-r from-purple-50 to-pink-50 h-40 rounded-lg flex flex-col items-center justify-center border border-purple-100 overflow-hidden">
                                                                                    <svg className="h-12 w-12 text-purple-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                                                    </svg>
                                                                                    <span className="text-xs text-purple-500 font-medium">Video</span>
                                                                                </div>
                                                                            );
                                                                        } else if (component.format === 'DOCUMENT') {
                                                                            return (
                                                                                <div key={index} className="bg-gradient-to-r from-yellow-50 to-yellow-100 h-40 rounded-lg flex flex-col items-center justify-center border border-yellow-100 overflow-hidden">
                                                                                    <svg className="h-12 w-12 text-yellow-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                                                    </svg>
                                                                                    <span className="text-xs text-yellow-500 font-medium">Document</span>
                                                                                </div>
                                                                            );
                                                                        }
                                                                    } else if (component.type === 'BODY' && component.text) {
                                                                        return (
                                                                            <div key={index} className="text-sm text-gray-700 whitespace-pre-line py-3">{component.text}</div>
                                                                        );
                                                                    } else if (component.type === 'FOOTER' && component.text) {
                                                                        return (
                                                                            <div key={index} className="text-xs text-gray-500 italic pt-2 border-t border-gray-100">{component.text}</div>
                                                                        );
                                                                    }
                                                                    return null;
                                                                })}
                                                            </div>
                                                        </div>

                                                        {template.createdAt && (
                                                            <div className="mt-3 text-xs text-gray-500">
                                                                Created: {template.createdAt.toLocaleString?.() || 'Unknown'}
                                                            </div>
                                                        )}

                                                        <div className="flex justify-between items-center mt-4">
                                                            {/* Delete Template Button */}
                                                            {/* <button
                                                                type="button"
                                                                onClick={() => {
                                                                    // setTemplateToDelete(template.name || null);
                                                                    // setShowDeleteConfirmation(true);
                                                                }}
                                                                className="mt-4 inline-flex items-center px-3 py-2 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                                            >
                                                                <svg className="h-4 w-4 text-red-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                </svg>
                                                                Delete Template
                                                            </button> */}

                                                            {/* Send Template Button */}
                                                            <button
                                                                type="button"
                                                                onClick={() => templateFormat(template)}
                                                                className="mt-2 inline-flex items-center px-3 py-2 border border-blue-300 shadow-sm text-sm font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                                            >
                                                                <svg className="h-4 w-4 text-blue-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16
                                                                    M4 14h16M4 18h16M4 22h16" />
                                                                </svg>
                                                                Send Template
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-8">
                                            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                            </svg>
                                            <p className="mt-2 text-gray-600">No templates found. Create your first template!</p>
                                            <button
                                                onClick={() => navigate('/integrations/whatsapp-business-cloud')}
                                                className="mt-3 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                            >
                                                Create Template
                                            </button>
                                        </div>
                                    )}

                                    {templates.length > 0 && (
                                        <div className="mt-6 text-right">
                                            <button
                                                onClick={() => handleTemplateClick()}
                                                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                            >
                                                <svg className="-ml-0.5 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                </svg>
                                                Refresh
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {templates.length > 0 && (
                                    <div className="flex justify-end space-x-3">
                                        <button
                                            onClick={() => handleCloseTemplateModal()}
                                            className="px-6 py-2 text-gray-700 border border-gray-300 rounded-full hover:bg-gray-50"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {(componentsState?.length > 0 || showMediaModal) && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-80 flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden">
                        <div className="p-6  overflow-y-auto" style={{ maxHeight: 'calc(90vh - 1rem)' }}>
                            <div className="flex justify-between items-center mb-6">
                                <h1 className="text-2xl font-bold">Component Variable Inputs & Media</h1>
                                <button onClick={() => handleComponentModalClose()} className="text-gray-500 hover:text-gray-700">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            {showMediaModal && (
                                <div className="mb-6">
                                    <label className="block text-sm text-gray-600 mb-2">Upload Media</label>
                                    <input
                                        type="file"
                                        accept={mediaAcceptedFormats(mediaFormat)}
                                        onChange={(e) => handleTemplateFileSelection(e)}
                                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500`}
                                    />
                                    {uploadingMedia && (
                                        <div className="mt-2 text-sm text-gray-500">
                                            Uploading media, please wait...
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="space-y-6 mb-6">
                                {componentsState.map((component, index) => (
                                    <div key={index} className="border rounded-lg p-4 bg-gray-50">
                                        <div className="flex items-center mb-3">
                                            <span className="bg-blue-500 text-white px-3 py-1 rounded text-sm">{component.type}</span>
                                            <span className="ml-3 text-gray-600">{component.inputFieldCount} variable(s)</span>
                                        </div>

                                        <div className="text-gray-700 mb-4 p-3 bg-white rounded border">
                                            <pre className="whitespace-pre-wrap">{component.text}</pre>
                                        </div>

                                        <div className="space-y-3">
                                            {component.variables.map((variable, vIndex) => (
                                                <div key={vIndex} className="flex flex-col">
                                                    <label className="mb-1 text-sm font-medium text-gray-700">
                                                        {variable.fullVar}
                                                    </label>
                                                    <input
                                                        type="text"
                                                        placeholder={`Enter value for ${variable.key}`}
                                                        value={inputValues[`${component.type}_${variable.key}`] || ''}
                                                        onChange={(e) => handleInputChange(component.type, variable.key, e.target.value)}
                                                        className="border rounded p-2 w-full"
                                                    />
                                                </div>
                                            ))}
                                        </div>

                                        <div className="mt-4 text-sm text-gray-600">
                                            <div className="font-semibold">Preview:</div>
                                            <div className="mt-1">
                                                {component.text.replace(/{{\s*([^{}]+)\s*}}/g, (match, key) => {
                                                    return inputValues[`${component.type}_${key}`] || match;
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => handleComponentModalClose()}
                                    className="px-6 py-2 text-gray-700 border border-gray-300 rounded-full hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                {uploadingMedia ? (
                                    <button
                                        disabled
                                        className={`px-6 py-2 text-white rounded-full bg-gray-400 cursor-not-allowed`}
                                    >
                                        Uploading...
                                    </button>
                                ) : (
                                    <>
                                        {sendLoading ? (
                                            <button
                                                disabled
                                                className={`px-6 py-2 text-white rounded-full bg-gray-400 cursor-not-allowed`}
                                            >
                                                Sending...
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => handleSendTemplate(selectedTemplate)}
                                                className={`px-6 py-2 text-white rounded-full bg-cyan-600 hover:bg-cyan-700`}
                                            >
                                                Send
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div >
    );
};

export default WhatsappBusinessCloudClient;